/**
 * Shared i18n bundle (AGENTS.md §Internationalization). Turkish is the base language (its
 * keys are authoritative — `MessageKey` is derived from `tr`); English overrides fall back to
 * Turkish when absent. Launch languages are tr + en only. Display strings must never be
 * hardcoded inline in the apps — add a key here and read it through {@link t}.
 *
 * Placeholders use `{name}` and are filled from the `params` argument.
 */

const tr = {
  // common
  'common.save': 'Kaydet',
  'common.cancel': 'İptal',
  'common.actions': 'İşlemler',
  'common.confirm': 'Onayla',
  'common.delete': 'Sil',
  'common.edit': 'Düzenle',
  'common.close': 'Kapat',
  'common.skipToContent': 'İçeriğe geç',
  'common.none': '—',
  'common.logout': 'Çıkış',
  'common.loading': 'Yükleniyor…',
  'common.error': 'Bir hata oluştu',
  'common.rateLimited': 'Çok fazla deneme yaptınız. Lütfen biraz sonra tekrar deneyin.',
  'common.turnstileFailed': 'Güvenlik doğrulaması başarısız oldu. Lütfen tekrar deneyin.',
  // Error boundary (M9) — last-resort UI when a render crashes; shared by both apps.
  'common.errorBoundary.title': 'Bir şeyler ters gitti',
  'common.errorBoundary.message': 'Beklenmeyen bir hata oluştu. Sayfayı yenilemeyi deneyin.',
  'common.errorBoundary.reload': 'Sayfayı yenile',
  'common.errorBoundary.requestId': 'Destek için referans',

  // Transactional email subjects (M9) — resolved server-side per recipient locale via `tf`
  // (ADR 0012/0023). Bodies live in the MJML templates (`apps/api/emails/*`); the admin copies
  // (depositReceivedAdmin, lotUnsold) go to a fixed operator inbox and stay Turkish.
  'email.depositConfirmed.subject': 'Teminatınız onaylandı',
  'email.depositRefunded.subject': 'Teminatınız iade edildi',
  'email.depositReceivedAdmin.subject': 'Yeni teminat alındı',
  'email.registrationReceivedAdmin.subject': 'Yeni üyelik başvurusu',
  'email.accountApproved.subject': 'Üyeliğiniz onaylandı',
  'email.passwordReset.subject': 'Parola sıfırlama',
  'email.membershipActivated.subject': 'Üyeliğiniz etkinleştirildi',
  'email.youWon.subject': 'Tebrikler, ihaleyi kazandınız',
  'email.lotUnsold.subject': 'Araç satılmadı',
  'common.yes': 'Evet',
  'common.no': 'Hayır',
  // Short day unit for countdowns past 24h ("29g 02:26:31") — a bare hour count is
  // unreadable at that range.
  'common.dayShort': 'g',

  // XLSX export (sheet names + column headers)
  'xlsx.sheet.details': 'Detaylar',
  'xlsx.sheet.deposits': 'Depozitolar',
  'xlsx.sheet.bids': 'Teklifler',
  'xlsx.col.order': 'Sıra',
  'xlsx.col.plate': 'Plaka',
  'xlsx.col.start': 'Başlangıç',
  'xlsx.col.currentBid': 'Güncel Teklif',
  'xlsx.col.bidCount': 'Teklif Sayısı',
  'xlsx.col.status': 'Durum',
  'xlsx.col.guaranteed': 'Kesin Satış',
  'xlsx.col.participant': 'Katılımcı',
  'xlsx.col.email': 'E-posta',
  'xlsx.col.amount': 'Tutar',
  'xlsx.col.date': 'Tarih',
  'xlsx.col.kind': 'Tür',
  'xlsx.col.time': 'Zaman',

  // nav / shell ({brand} is injected from env config — ADR 0015)
  'app.title': '{brand} Yönetim',
  // Brand-less variant for placements next to the logo lockup (the wordmark
  // already says the brand — don't repeat it).
  'app.nav.title': 'Yönetim',
  'nav.dashboard': 'Genel Bakış',
  // "İhale", not "Satış": the domain word for an auction (docs/10-glossary.md). The code-side
  // name stays `sales` — only the label is Turkish-correct.
  'nav.sales': 'İhaleler',
  'nav.submissions': 'Başvurular',
  'nav.users': 'Kullanıcılar',
  'nav.firms': 'Firmalar',
  'nav.config': 'Ayarlar',
  'nav.toggle': 'Menüyü daralt/genişlet',
  // Theme switch — the label names the theme the click switches TO (docs/15 §5).
  'theme.switchToDark': 'Koyu tema',
  'theme.switchToLight': 'Açık tema',

  // dashboard (admin home) — the operator's "kaç ihale aktif, ne satıldı" overview
  'dashboard.title': 'Genel Bakış',
  'dashboard.section.sales': 'İhaleler',
  'dashboard.section.lots': 'Araçlar',
  'dashboard.section.sold': 'Satış Tutarı',
  'dashboard.sales.active': 'Aktif',
  'dashboard.sales.activeHint': 'Canlı, duraklatılmış veya yayında',
  'dashboard.sales.live': 'Canlı',
  'dashboard.sales.published': 'Yayında',
  'dashboard.sales.paused': 'Duraklatılmış',
  'dashboard.sales.draft': 'Taslak',
  'dashboard.sales.passive': 'Pasif',
  'dashboard.sales.passiveHint': 'Tamamlanan veya iptal edilen',
  'dashboard.sales.closed': 'Tamamlanan',
  'dashboard.sales.cancelled': 'İptal edilen',
  'dashboard.lots.total': 'Toplam araç',
  'dashboard.lots.open': 'Açık',
  'dashboard.lots.scheduled': 'Sırada',
  'dashboard.lots.sold': 'Satılan',
  'dashboard.lots.unsold': 'Satılmayan',
  'dashboard.sold.count': '{n} araç satıldı',
  'dashboard.sold.empty': 'Henüz satılan araç yok.',
  'dashboard.live.title': 'Şu an devam eden ihaleler',
  'dashboard.live.empty': 'Devam eden ihale yok.',
  'dashboard.viewAll': 'Tüm ihaleler →',

  // login
  'login.title': 'Yönetim — Giriş',
  'login.email': 'E-posta',
  'login.password': 'Parola',
  'login.submit': 'Giriş',
  'login.submitting': 'Giriş yapılıyor…',
  'login.failed': 'Giriş başarısız',
  // Password reset (M19, audit Y-06). Shared by both apps: the flow and its wording are the
  // same, and the reset link lands on whichever origin the request came from.
  'reset.forgotLink': 'Parolamı unuttum',
  'reset.forgotTitle': 'Parola sıfırlama',
  'reset.forgotIntro':
    'Hesabınızın e-posta adresini girin. Kayıtlı bir hesap varsa sıfırlama bağlantısı gönderilir.',
  'reset.forgotSubmit': 'Sıfırlama bağlantısı gönder',
  // Deliberately says "if an account exists" — the server answers identically either way, and
  // the copy must not promise more than the response does.
  'reset.forgotSent':
    'Kayıtlı bir hesap varsa sıfırlama bağlantısı e-posta adresinize gönderildi. Bağlantı 30 dakika geçerlidir.',
  'reset.resetTitle': 'Yeni parola belirleyin',
  'reset.newPassword': 'Yeni parola',
  'reset.confirmPassword': 'Yeni parola (tekrar)',
  'reset.submit': 'Parolayı güncelle',
  'reset.mismatch': 'Parolalar eşleşmiyor',
  'reset.tooShort': 'Parola en az 8 karakter olmalı',
  'reset.done': 'Parolanız güncellendi. Yeni parolanızla giriş yapabilirsiniz.',
  'reset.backToLogin': 'Girişe dön',
  'reset.missingToken': 'Bağlantı geçersiz. Sıfırlamayı yeniden başlatın.',
  'reset.failed': 'Parola güncellenemedi',
  'reset.invalidToken':
    'Bu sıfırlama bağlantısı geçersiz veya süresi dolmuş. Sıfırlamayı yeniden başlatın.',

  // sales list — column headers name the thing they hold ("Kod"/"Başlık" alone read as
  // someone else's field names to an operator).
  'sales.title': 'İhaleler',
  'sales.new': 'Yeni İhale',
  'sales.tab.future': 'Yaklaşan',
  'sales.tab.past': 'Geçmiş',
  'sales.tab.draft': 'Taslak',
  'sales.col.code': 'İhale Kodu',
  'sales.col.title': 'İhale Adı',
  'sales.col.style': 'Akış',
  'sales.col.start': 'Başlangıç',
  'sales.col.lots': 'Araç',
  'sales.col.status': 'Durum',
  'sales.untitled': '(adsız)',
  'sales.cancelConfirm': '"{name}" iptal edilsin mi?',
  'sales.cancelled': 'İhale iptal edildi',
  'sales.cancelFailed': 'İptal başarısız',
  'sales.cancelTitle': 'İhaleyi iptal et',
  'sales.empty': 'Bu sekmede ihale yok.',
  'sales.emptyFiltered': 'Seçtiğiniz filtrelere uyan ihale yok.',
  'sales.filter.status': 'Durum',
  'sales.filter.from': 'Başlangıç (en erken)',
  'sales.filter.to': 'Başlangıç (en geç)',
  'sales.filter.clear': 'Filtreleri temizle',

  // Raw lifecycle state, for the sales-list status filter. Deliberately NOT the
  // `presentation.*` labels: those collapse PUBLISHED and a not-yet-started LIVE sale into
  // the same "Hazırlanıyor", which would put two identical options in one dropdown.
  'saleState.DRAFT': 'Taslak',
  'saleState.PUBLISHED': 'Yayında',
  'saleState.LIVE': 'Canlı',
  'saleState.PAUSED': 'Duraklatıldı',
  'saleState.CLOSED': 'Tamamlandı',
  'saleState.CANCELLED': 'İptal edildi',

  // presentation status
  'presentation.draft': 'Taslak',
  'presentation.preparing': 'Hazırlanıyor',
  'presentation.inprogress': 'Devam ediyor',
  'presentation.paused': 'Duraklatıldı',
  'presentation.done': 'Tamamlandı',
  'presentation.cancelled': 'İptal',

  // lot state
  'lotState.SCHEDULED': 'Sırada',
  'lotState.OPEN': 'Açık',
  'lotState.SOLD': 'Satıldı',
  'lotState.UNSOLD': 'Satılmadı',
  'lotState.REMOVED': 'Çıkarıldı',

  // sale style
  'style.parallel': 'Paralel',
  'style.serial': 'Sıralı',
  'style.serial-10': 'Sıralı (10 dk)',
  'style.serial-with-cooldown': 'Sıralı (aralıklı)',

  // deposit state
  'depositState.PENDING': 'Beklemede',
  'depositState.HELD': 'Bloke',
  'depositState.CAPTURED': 'Tahsil edildi',
  'depositState.REFUNDED': 'İade edildi',
  'depositState.VOIDED': 'İptal',

  // bid kind
  'bidKind.bid': 'Teklif',
  'bidKind.early-bid': 'Erken teklif',
  'bidKind.free-bid': 'Serbest teklif',

  // RBAC roles (display names)
  'role.ADMIN': 'Yönetici',
  'role.DEVELOPER': 'Geliştirici',
  'role.EDITOR': 'Editör',
  'role.LOGISTIC_EDITOR': 'Lojistik Editörü',
  'role.WEB_EDITOR': 'Web Editörü',
  'role.FIRM': 'Firma',
  'role.FIRM_AUCTION': 'Firma (İhale)',
  'role.FIRM_USER': 'Firma (Kullanıcı)',
  'role.FIRM_VENDOR': 'Firma (Satıcı)',
  'role.GOLDBIDDER': 'Gold Üye',
  'role.BIDDER': 'Katılımcı',
  'role.USER': 'Kullanıcı',
  'role.ANONYMOUS': 'Anonim',
  'role.CLIENT': 'İstemci',

  // new sale
  'newSale.title': 'Yeni İhale',
  'newSale.general': 'Genel',
  'newSale.titleField': 'İhale Adı',
  'newSale.location': 'Konum',
  'newSale.style': 'Akış',
  'newSale.joint': 'Ortak konferans odası',
  'newSale.listing': 'Listeleme zamanı',
  'newSale.start': 'Başlangıç zamanı',
  'newSale.defaultReserve': 'Varsayılan rezerv (₺, opsiyonel)',
  'newSale.targetCompanies': 'Hedef firmalar (opsiyonel)',
  'newSale.targetPlaceholder': 'Seçim yoksa satış herkese açıktır.',
  'newSale.vehicles': 'Araçlar ({n} seçili · ~{min} dk)',
  'newSale.noVehicles': 'Uygun araç yok.',
  'newSale.draftSaved': 'Taslak kaydedildi · {time}',
  'newSale.draftFailed': 'Taslak kaydedilemedi',
  'newSale.vehicleFilter': 'Plaka veya marka ara…',
  'newSale.col.plate': 'Plaka',
  'newSale.col.vehicle': 'Araç',
  'newSale.col.start': 'Başlangıç',
  'newSale.col.reserve': 'Rezerv',
  'newSale.reservePlaceholder': 'Rezerv ₺',
  'newSale.guaranteed': 'Kesin satış',
  'newSale.submit': 'İhaleyi Oluştur',
  'newSale.created': 'İhale oluşturuldu',
  'newSale.createFailed': 'Oluşturulamadı',
  'newSale.draftClearFailed': 'İhale oluşturuldu, ancak taslak temizlenemedi — lütfen taslağı elle temizleyin',
  'newSale.needVehicle': 'En az bir araç seçin',
  'newSale.draftVehiclesDropped': 'Taslaktaki {count} araç artık uygun değil (başka bir ihalede) — çıkarıldı',

  // sale detail / live console
  'saleDetail.pause': 'Duraklat',
  'saleDetail.resume': 'Devam Et',
  'saleDetail.edit': 'Düzenle',
  'saleDetail.editTitle': 'İhaleyi Düzenle',
  'saleDetail.updated': 'Güncellendi',
  'saleDetail.editFailed': 'Güncellenemedi',
  'saleDetail.export': 'Excel İndir',
  'saleDetail.joinJointConf': 'Ortak konferans odasına katıl →',
  'saleDetail.joinLotConf': 'Konferans →',
  'saleDetail.deposits': 'Depozitolar',
  'saleDetail.bidHistory': 'Teklif Geçmişi',
  'saleDetail.col.participant': 'Katılımcı',
  'saleDetail.col.plate': 'Plaka',
  'saleDetail.col.amount': 'Tutar',
  'saleDetail.col.status': 'Durum',
  'saleDetail.col.order': 'Sıra',
  'saleDetail.col.kind': 'Tür',
  'saleDetail.col.time': 'Zaman',
  'saleDetail.currentBid': 'Güncel teklif',
  'saleDetail.bidCount': 'Teklif sayısı',
  'saleDetail.remaining': 'Kalan süre',
  'saleDetail.guaranteed': 'Kesin satış',
  'saleDetail.action.bid': '+1 Teklif',
  'saleDetail.action.free': 'Serbest',
  'saleDetail.action.jump': 'Hedefe yükselt',
  'saleDetail.action.reserve': 'Rezerv',
  'saleDetail.action.takeControl': 'Kontrolü al',
  'saleDetail.action.releaseControl': 'Kontrolü bırak',
  'saleDetail.action.hammer': 'Çekiç',

  // Reserve + take-control explainers. Both controls were reported as unreadable ("Rezerv ne
  // oluyor", "Kontrolü al bırak neye yarıyor") — the copy states what the button does and,
  // for the reserve, what pressing it a second time will and will not change.
  'saleDetail.reserve.help':
    'Rezerv, katılımcıların görmediği gizli taban fiyattır. Güncel teklif bu tutara ulaştığında araç otomatik olarak kesin satışa geçer.',
  'saleDetail.reserve.current': 'Mevcut rezerv: {amount}',
  'saleDetail.reserve.none': 'Bu araçta rezerv tanımlı değil.',
  'saleDetail.reserve.crossedTitle': 'Rezerv aşıldı',
  'saleDetail.reserve.crossedHelp':
    'Güncel teklif rezervi geçtiği için araç kesin satışta. Rezervi yeniden girmek bunu geri almaz — araç yine en yüksek teklifi verene satılır.',
  'saleDetail.reserve.guaranteedHelp':
    'Bu araç kesin satış olarak açıldı; rezervden bağımsız olarak en yüksek teklifi verene satılır.',
  'saleDetail.reserve.lowerHint':
    'Rezervi düşürmek aracı satılabilir hale getirir; yükseltmek yalnızca henüz aşılmamışsa etkilidir.',
  'saleDetail.control.on': 'Kontrol sizde',
  'saleDetail.control.help':
    'Kontrolü aldığınızda lotu istediğiniz an "Çekiç" ile kapatabilirsiniz ve son 30 saniyede gelen her teklif süreyi 30 saniye uzatır. Kontrolü bırakırsanız lot yine kendi bitiş saatinde otomatik kapanır.',
  'saleDetail.action.remove': 'Çıkar',
  'saleDetail.action.reAdd': 'Tekrar ekle',
  'saleDetail.prompt.free': 'Serbest teklif ({currency}):',
  'saleDetail.prompt.jump': 'Hedef tutar ({currency}):',
  'saleDetail.prompt.reserve': 'Rezerv ({currency}):',
  'saleDetail.confirm.remove': 'Lot çıkarılsın mı?',
  'saleDetail.confirm.reAddGuaranteed': 'Kesin satış olarak eklensin mi? İptal = normal.',
  'saleDetail.prompt.reAddReserve': 'Yeni rezerv ({currency}, düşürmek için):',
  'saleDetail.bidCountShort': '{n} teklif',
  'saleDetail.remainingShort': '{time} kaldı',
  'saleDetail.noDeposits': 'Bu ihalede teminat yok.',
  'saleDetail.noBids': 'Henüz teklif yok.',
  'saleDetail.dialog.invalidAmount': 'Geçerli bir tutar girin',
  'saleDetail.dialog.mustExceedTop': 'Mevcut teklifin üzerinde olmalı',
  'saleDetail.dialog.reAddGuaranteed': 'Kesin satış (garantili) olarak ekle',
  'saleDetail.dialog.reAddReserveHint': 'Boş bırakılırsa kaynak lotun rezervi devralınır.',
  'saleDetail.confAvailable': 'Konferans odası hazır',
  'saleDetail.actionFailed': 'İşlem başarısız',
  'saleDetail.exportFailed': 'Dışa aktarılamadı',

  // users
  'users.title': 'Kullanıcılar',
  'users.new': 'Yeni Kullanıcı',
  'users.edit': 'Kullanıcı Düzenle',
  // Approval gate (M15) — admin-only surface, so tr-only like the rest of this app.
  'users.tab.pending': 'Bekleyen',
  'users.tab.approved': 'Onaylı',
  'users.tab.rejected': 'Reddedilen',
  'users.status': 'Durum',
  'users.approve': 'Onayla',
  'users.reject': 'Reddet',
  'users.rejectReason': 'Ret gerekçesi (isteğe bağlı)',
  'users.rejectConfirm': 'Bu başvuru reddedilsin mi? Kullanıcının açık oturumları kapatılır.',
  'users.approved': 'Başvuru onaylandı',
  'users.rejected': 'Başvuru reddedildi',
  'users.emptyPending': 'Bekleyen başvuru yok.',
  'users.col.email': 'E-posta',
  'users.col.name': 'Ad',
  'users.col.roles': 'Roller',
  'users.field.first': 'Ad',
  'users.field.last': 'Soyad',
  'users.field.password': 'Parola',
  'users.field.newPassword': 'Yeni parola (opsiyonel)',
  'users.field.roles': 'Roller',
  'users.field.companies': 'Firmalar',
  'users.searchPlaceholder': 'E-posta veya isim ara…',
  'users.roleGroup.staff': 'Yönetim',
  'users.roleGroup.firm': 'Firma',
  'users.roleGroup.participant': 'Katılımcı',
  // users — list filters (rol / firma / üyelik)
  'users.filter.search': 'Ara',
  'users.filter.role': 'Rol',
  'users.filter.roleAll': 'Tüm roller',
  'users.filter.firm': 'Firma',
  'users.filter.firmAll': 'Tüm firmalar',
  'users.filter.firmNone': 'Firmasız',
  'users.filter.membership': 'Üyelik',
  'users.filter.membershipAll': 'Tüm üyelikler',
  'users.filter.membershipNone': 'Üyeliksiz',
  'users.filter.clear': 'Filtreleri temizle',
  'users.count': '{n} kullanıcı',
  'users.noMatch': 'Filtrelerle eşleşen kullanıcı yok.',
  'companyPicker.search': 'Firma ara…',
  'companyPicker.empty': 'Firma bulunamadı.',
  'companyPicker.remove': '{name} seçimini kaldır',
  'users.created': 'Kullanıcı oluşturuldu',
  'users.updated': 'Güncellendi',
  'users.deleted': 'Silindi',
  'users.deleteConfirm': '{email} silinsin mi?',
  'users.empty': 'Kullanıcı yok.',

  // users — bidder profile (M10)
  'users.detail.notFound': 'Kullanıcı bulunamadı.',
  'users.detail.viewAudit': 'Denetim kaydını gör',
  'users.detail.deposits': 'Teminatlar',
  'users.detail.bids': 'Teklif Geçmişi',
  'users.detail.noDeposits': 'Teminat yok.',
  'users.detail.noBids': 'Teklif yok.',
  'users.detail.col.sale': 'İhale',
  'users.detail.col.plate': 'Plaka',
  'users.detail.col.amount': 'Tutar',
  'users.detail.col.state': 'Durum',
  'users.detail.col.date': 'Tarih',
  'users.detail.col.kind': 'Tür',
  'users.detail.col.lotState': 'Lot Durumu',

  // firms (M13) — partner firm records and the ingestion API keys that hang off them
  'firms.title': 'Firmalar',
  'firms.new': 'Yeni Firma',
  'firms.edit': 'Firma Düzenle',
  'firms.searchPlaceholder': 'Firma adı, vergi no veya e-posta ara…',
  'firms.showArchived': 'Arşivlenenleri göster',
  'firms.empty': 'Firma yok.',
  'firms.notFound': 'Firma bulunamadı.',
  'firms.col.name': 'Firma',
  'firms.col.taxNumber': 'Vergi No',
  'firms.col.contact': 'İletişim',
  'firms.col.keys': 'Anahtar',
  'firms.field.name': 'Firma adı',
  'firms.field.taxAdmin': 'Vergi dairesi',
  'firms.field.taxNumber': 'Vergi no',
  'firms.field.contactEmail': 'İletişim e-postası',
  'firms.field.contactPhone': 'Telefon',
  'firms.field.address': 'Adres',
  'firms.field.editorial': 'Editoryal firma',
  'firms.created': 'Firma oluşturuldu',
  'firms.updated': 'Güncellendi',
  'firms.archive': 'Arşivle',
  'firms.archived': 'Arşivlendi',
  'firms.archivedBadge': 'Arşivli',
  'firms.archiveConfirm':
    '{name} arşivlenecek ve firmanın bütün API anahtarları iptal edilecek. Firma araç göndermeyi anında durdurur.',
  'firms.restore': 'Arşivden çıkar',
  'firms.restored': 'Arşivden çıkarıldı',
  'firms.restoreNote': 'Arşivden çıkarmak eski anahtarları geri getirmez; yeni anahtar üretin.',

  // firms — API keys
  'firms.keys.title': 'API Anahtarları',
  'firms.keys.intro':
    'Firmanın kendi sisteminden araç göndermek için kullandığı anahtar. Anahtarı üretip firmaya siz iletirsiniz.',
  'firms.keys.new': 'Anahtar Üret',
  'firms.keys.label': 'Etiket (opsiyonel)',
  'firms.keys.labelPlaceholder': 'ör. stok-servisi',
  'firms.keys.empty': 'Bu firmanın anahtarı yok.',
  'firms.keys.col.label': 'Etiket',
  'firms.keys.col.created': 'Oluşturuldu',
  'firms.keys.col.lastUsed': 'Son kullanım',
  'firms.keys.col.state': 'Durum',
  'firms.keys.state.active': 'Geçerli',
  'firms.keys.state.revoked': 'İptal',
  'firms.keys.neverUsed': 'Hiç kullanılmadı',
  'firms.keys.revoke': 'İptal Et',
  'firms.keys.revoked': 'Anahtar iptal edildi',
  'firms.keys.revokeConfirm':
    'Bu anahtar anında geçersiz olur ve geri alınamaz. Firmanın entegrasyonu yeni anahtara geçmeden araç gönderemez.',
  'firms.keys.createdTitle': 'Anahtar üretildi',
  'firms.keys.createdWarning':
    'Bu anahtar yalnızca bir kez gösterilir — bizde yalnızca özeti saklanır. Şimdi kopyalayıp firmaya güvenli bir kanaldan iletin.',
  'firms.keys.archivedNote': 'Arşivli firmaya anahtar üretilemez.',
  'common.copy': 'Kopyala',
  'common.copied': 'Kopyalandı',
  'common.all': 'Tümü',

  // deposit refund (M10)
  'deposits.refund.action': 'İade Et',
  'deposits.refund.confirm': '{sale} ihalesindeki {amount} teminat iade edilsin mi?',
  'deposits.refund.voided': 'Teminat iptal edildi (aynı gün void)',
  'deposits.refund.refunded': 'Teminat iade edildi',
  'deposits.refund.failed': 'İade başarısız',
  // Coded refund outcomes (mapped from the error envelope's `error` label — the server's
  // message strings are English-only).
  'deposits.refund.blockedTopBidder':
    'Kullanıcı bu lotta en yüksek teklifin sahibi — iade için lotun kapanmasını bekleyin.',
  'deposits.refund.declined': 'Banka iadeyi reddetti.',
  'deposits.refund.ambiguous':
    'Banka yanıtı alınamadı — teminat kilitlendi, banka paneli üzerinden mutabakat gerekiyor.',
  'deposits.refund.inProgress': 'Bu teminat için bir iade zaten sürüyor.',
  'deposits.refund.notRefundable': 'Teminat bu durumda iade edilemez.',
  // Ambiguous refund (bank call failed mid-flight): deposit terminal, payment still SUCCESSFUL.
  'deposits.orderIdLabel': 'Banka sipariş no',
  'deposits.state.reconcile': 'Mutabakat bekliyor',
  'deposits.state.reconcileHint':
    'İade sırasında banka yanıtı alınamadı — banka paneli üzerinden mutabakat gerekiyor.',
  // Status-inquiry reconciliation of an ambiguous refund (ADR 0011 amendment)
  'deposits.reconcile.action': 'Mutabakat',
  'deposits.reconcile.confirm': 'Banka sipariş durumu sorgulanıp teminat mutabakatı yapılsın mı?',
  'deposits.reconcile.confirmedVoided': 'Banka iptali doğruladı — ödeme void olarak kapatıldı.',
  'deposits.reconcile.confirmedRefunded': 'Banka iadeyi doğruladı — ödeme iade olarak kapatıldı.',
  'deposits.reconcile.reverted':
    'Bankada iade görünmüyor — teminat tekrar HELD durumuna alındı; iadeyi yeniden deneyebilirsiniz.',
  'deposits.reconcile.ambiguous':
    'Banka sorgusu net yanıt vermedi — durum değişmedi, banka paneli üzerinden kontrol edin.',
  'deposits.reconcile.notPending': 'Bu teminat için mutabakat gerekmiyor.',
  'deposits.reconcile.inProgress':
    'Bu teminat için bir iade işlemi şu anda sürüyor — tamamlanınca tekrar deneyin.',
  'deposits.reconcile.cooldown':
    'Banka ödemeyi hâlâ tahsil edilmiş gösteriyor — doğrulamak için 10 dakika sonra mutabakatı tekrarlayın.',
  'deposits.reconcile.failed': 'Mutabakat başarısız',

  // payment attempts + stuck-PRE3D reconcile (post-M10, ADR 0011 amendment)
  'users.detail.payments': 'Ödemeler',
  'users.detail.noPayments': 'Ödeme yok.',
  'users.detail.col.order': 'Sipariş no',
  'paymentState.WAITING': 'Bekliyor',
  'paymentState.PRE3D': '3DS sürüyor',
  'paymentState.SUCCESSFUL': 'Başarılı',
  'paymentState.FAIL': 'Başarısız',
  'paymentState.VOIDED': 'İptal',
  'paymentState.REFUNDED': 'İade edildi',
  'payments.kind.deposit': 'Teminat',
  'payments.kind.membership': 'Üyelik',
  'payments.state.stuck': 'Takılı (3DS yanıtı kayıp)',
  'payments.state.stuckHint':
    '3DS geri çağrısı ulaşmadı — banka tahsil etmiş olabilir; mutabakat gerekiyor. Bu kayıt çözülene dek kullanıcı yeni ödeme başlatamaz.',
  'payments.reconcile.action': 'Mutabakat',
  'payments.reconcile.confirm': 'Banka sipariş durumu sorgulanıp bu ödeme mutabakatı yapılsın mı?',
  'payments.reconcile.confirmed':
    'Banka tahsilatı doğruladı — ödeme tamamlandı, hak kazanımı işlendi.',
  'payments.reconcile.returned':
    'Banka parayı iade etmiş — ödeme kapatıldı; kullanıcı yeni deneme yapabilir.',
  'payments.reconcile.ambiguous':
    'Banka sorgusu net yanıt vermedi — durum değişmedi, banka paneli üzerinden kontrol edin.',
  'payments.reconcile.notPending': 'Bu ödeme için mutabakat gerekmiyor.',
  'payments.reconcile.failed': 'Mutabakat başarısız',
  'payments.markFailed.action': 'Başarısız işaretle',
  'payments.markFailed.confirm':
    'DİKKAT: Yalnızca sipariş bankada HİÇ tahsil edilmediyse kullanın (banka panelinden doğrulayın). Ödeme FAIL olarak kapatılacak ve kullanıcı yeni deneme yapabilecek. Tahsilat varsa bu işlem çifte tahsilata yol açabilir. Devam edilsin mi?',
  'payments.markFailed.done': 'Ödeme başarısız olarak kapatıldı — kullanıcı yeni deneme yapabilir.',

  // audit viewer (M10)
  'nav.audit': 'Denetim',
  'audit.title': 'Denetim Kaydı',
  'audit.filter.entityType': 'Varlık türü',
  'audit.filter.entityId': 'Varlık id',
  'audit.filter.actorId': 'Kullanıcı id',
  'audit.filter.actorCompany': 'Firma (API)',
  'audit.filter.action': 'İşlem',
  'audit.filter.from': 'Başlangıç',
  'audit.filter.to': 'Bitiş',
  'audit.col.time': 'Zaman',
  'audit.col.actor': 'Kullanıcı',
  'audit.col.entity': 'Varlık',
  'audit.col.action': 'İşlem',
  'audit.col.details': 'Detay',
  'audit.expand': 'Detayı göster',
  'audit.redacted': '—',
  'audit.systemActor': 'sistem',
  'audit.firmActor': '{name} (firma anahtarı)',
  'audit.empty': 'Kayıt bulunamadı.',
  'audit.filter.clear': 'Temizle',
  'audit.filter.active': '{n} filtre etkin',
  'audit.detailsTitle': 'Kayıt detayı',
  'audit.before': 'Önce',
  'audit.after': 'Sonra',
  'saleDetail.viewAudit': 'Denetim kaydı',

  // Audit entity types. `audit_log.entity_type` is free text (retention-tolerant history), so
  // these labels are a best-effort map over the values the API writes today — an unrecognized
  // legacy value renders raw rather than blank (see `lib/labels.ts`).
  'audit.entity.sale': 'İhale',
  'audit.entity.lot': 'İhale aracı',
  'audit.entity.bid': 'Teklif',
  'audit.entity.deposit': 'Teminat',
  'audit.entity.payment': 'Ödeme',
  'audit.entity.user': 'Kullanıcı',
  'audit.entity.config': 'Ayar',
  'audit.entity.vehicle': 'Araç',
  'audit.entity.submission': 'Başvuru',
  'audit.entity.company': 'Firma',
  'audit.entity.api-key': 'API anahtarı',

  // Audit actions. Same free-text caveat as the entity types above: this covers every action
  // the API writes today; anything else falls back to the stored slug.
  'audit.action.create': 'Oluşturuldu',
  'audit.action.update': 'Güncellendi',
  'audit.action.delete': 'Silindi',
  'audit.action.approve': 'Onaylandı',
  'audit.action.reject': 'Reddedildi',
  'audit.action.archive': 'Arşivlendi',
  'audit.action.restore': 'Arşivden çıkarıldı',
  'audit.action.register': 'Kayıt oluşturuldu',
  'audit.action.membership-approved': 'Üyelik onaylandı',
  'audit.action.membership-rejected': 'Üyelik reddedildi',
  'audit.action.initiate': 'Başlatıldı',
  'audit.action.grant-role': 'Rol verildi',
  'audit.action.bid-placed': 'Teklif verildi',
  'audit.action.set-reserve': 'Rezerv belirlendi',
  'audit.action.take-control': 'Kontrol alındı',
  'audit.action.pause': 'Duraklatıldı',
  'audit.action.resume': 'Devam ettirildi',
  'audit.action.re-add': 'Yeniden eklendi',
  'audit.action.settlement-completed': 'Tahsilat tamamlandı',
  'audit.action.media-add': 'Medya eklendi',
  'audit.action.media-update': 'Medya güncellendi',
  'audit.action.media-delete': 'Medya silindi',
  'audit.action.media-upload': 'Medya yüklendi',
  'audit.action.provision-conference': 'Konferans oluşturuldu',
  'audit.action.teardown-conference': 'Konferans kapatıldı',
  'audit.action.teardown-conference-failed': 'Konferans kapatılamadı',
  'audit.action.recording-capture-exhausted': 'Kayıt alma denemeleri tükendi',
  'audit.action.success': 'Ödeme başarılı',
  'audit.action.fail': 'Ödeme başarısız',
  'audit.action.revive': 'Yeniden etkinleştirildi',
  'audit.action.duplicate-payment': 'Mükerrer ödeme',
  'audit.action.refund': 'İade edildi',
  'audit.action.refund-ambiguous': 'İade belirsiz',
  'audit.action.refund-declined': 'İade reddedildi',
  'audit.action.refund-topbidder-conflict': 'İade — en yüksek teklif çakışması',
  'audit.action.void': 'İptal (void)',
  'audit.action.credit': 'İade (credit)',
  'audit.action.reconcile-ambiguous': 'Mutabakat belirsiz',
  'audit.action.reconcile-attempt-failed': 'Mutabakat denemesi başarısız',
  'audit.action.reconcile-charged-pending': 'Mutabakat — tahsilat teyidi bekleniyor',
  'audit.action.reconcile-confirmed': 'Mutabakat doğrulandı',
  'audit.action.reconcile-returned': 'Mutabakat — iade doğrulandı',
  'audit.action.reconcile-reverted': 'Mutabakat geri alındı',
  'audit.action.reconcile-mark-failed': 'Mutabakatta başarısız işaretlendi',
  'audit.action.push-subscription-set': 'Push aboneliği tanımlandı',
  'audit.action.push-subscription-reassigned': 'Push aboneliği devredildi',
  'audit.action.push-subscription-cleared': 'Push aboneliği temizlendi',
  'audit.action.push-channel-cleared': 'Bildirim kanalı temizlendi',
  'audit.action.fcm-token-set': 'FCM anahtarı tanımlandı',
  'audit.action.fcm-token-reassigned': 'FCM anahtarı devredildi',
  'audit.action.fcm-token-cleared': 'FCM anahtarı temizlendi',
  'audit.action.locale-updated': 'Dil tercihi güncellendi',
  'audit.action.retention-anonymize-batch': 'Saklama — anonimleştirme partisi',
  'audit.action.retention-anonymize-consent-batch': 'Saklama — onay kaydı anonimleştirme partisi',
  'audit.action.retention-purge-batch': 'Saklama — silme partisi',

  // list search + pagination (M10)
  'sales.search.placeholder': 'İhale kodu, adı veya konum ara…',
  'pagination.range': '{from}–{to} / {total}',
  'pagination.prev': 'Önceki sayfa',
  'pagination.next': 'Sonraki sayfa',

  // config
  'config.title': 'Ayarlar',
  'config.deposit.title': 'Depozito Tutarı',
  'config.deposit.label': 'Depozito (₺)',
  'config.deposit.saved': 'Depozito tutarı kaydedildi',
  'config.memberships.title': 'Üyelik Paketleri',
  'config.memberships.saved': 'Üyelik paketleri kaydedildi',
  'config.col.slug': 'Kod (slug)',
  'config.col.name': 'Ad',
  'config.col.price': 'Fiyat (₺)',
  'config.col.role': 'Rol',
  'config.col.active': 'Aktif',
  'config.addPackage': 'Paket Ekle',
  'config.unsaved': 'Kaydedilmemiş değişiklikler var',
  'config.removePackage': 'Paketi kaldır',
  'config.removePackageConfirm': 'Paket listeden kaldırılacak; değişiklik Kaydet ile kalıcı olur.',

  // Showcase marketing counters (M16). Admin-only surface, so Turkish only — same as the
  // rest of the admin dictionary.
  'config.counters.title': 'Vitrin Sayaçları',
  'config.counters.intro':
    'Giriş yapmamış ziyaretçinin gördüğü toplam rakamlar. Her sayaç için bir başlangıç değeri belirleyebilirsiniz; ziyaretçiye gösterilen sayı, başlangıç değeri ile gerçek sayının toplamıdır.',
  'config.counters.saved': 'Vitrin sayaçları kaydedildi',
  'config.counters.col.counter': 'Sayaç',
  'config.counters.col.show': 'Göster',
  'config.counters.col.baseline': 'Başlangıç değeri',
  'config.counters.col.real': 'Gerçek',
  'config.counters.col.shown': 'Ziyaretçi görür',
  'config.counters.salesHeld': 'Yapılan ihale',
  'config.counters.vehiclesSold': 'Satılan araç',
  'config.counters.hiddenNote': 'Kapalı sayaçlar vitrinde hiç görünmez.',
  'config.counters.allOff': 'Şu anda hiçbir sayaç gösterilmiyor; vitrinde bu bölüm çıkmaz.',

  // submissions
  // The page is not a pending-only queue — it has Bekleyen/Onaylanan/Reddedilen tabs — so the
  // heading names *what* these records are (vehicles firms sent in) and the subline says where
  // they come from and what approving one does. "Bekleyen" belongs to the tab, not the title.
  'submissions.title': 'Araç Başvuruları',
  'submissions.subtitle':
    'İş ortağı firmaların kendi sistemlerinden gönderdiği araçlar. Onayladığınız araç, hedeflediği ihaleye “Sırada” olarak eklenir; reddedilen araç ihaleye hiç girmez.',
  'submissions.col.plate': 'Plaka',
  'submissions.col.vehicle': 'Araç',
  'submissions.col.company': 'Gönderen firma',
  'submissions.col.start': 'Başlangıç',
  'submissions.approve': 'Onayla',
  'submissions.reject': 'Reddet',
  'submissions.approved': 'Başvuru onaylandı',
  'submissions.rejected': 'Başvuru reddedildi',
  'submissions.rejectReason': 'Red gerekçesi (opsiyonel):',
  // One empty message per tab — "Bekleyen başvuru yok." on the Onaylanan tab told the
  // moderator about the wrong list.
  'submissions.empty.queued': 'Bekleyen başvuru yok.',
  'submissions.empty.approved': 'Onaylanmış başvuru yok.',
  'submissions.empty.rejected': 'Reddedilmiş başvuru yok.',
  'submissions.tab.queued': 'Bekleyen',
  'submissions.tab.approved': 'Onaylanan',
  'submissions.tab.rejected': 'Reddedilen',
  'submissions.status.queued': 'Bekliyor',
  'submissions.status.scheduled': 'Planlandı',
  'submissions.status.live': 'Yayında',
  'submissions.status.sold': 'Satıldı',
  'submissions.status.unsold': 'Satılmadı',
  'submissions.status.rejected': 'Reddedildi',
  'submissions.review': 'İncele',
  'submissions.detailTitle': 'Başvuru detayı',
  'submissions.detail.clientRef': 'Müşteri referansı',
  'submissions.detail.created': 'Gönderim',
  'submissions.detail.decided': 'Karar',

  // === Bidder web (M7) ======================================================
  'bidder.appTitle': '{brand} Açık Artırma',
  // Social/discovery metadata (M19) — rendered into the served HTML at build time, not by the
  // running app, so a crawler and a link preview see it.
  'bidder.meta.description':
    '{brand} açık artırma platformu — araç müzayedelerini canlı takip edin, teklif verin.',
  // nav / shell (the header shows the logo lockup alone — no text qualifier)
  'bidder.nav.auctions': 'Açık Artırmalar',
  'bidder.nav.dashboard': 'Panelim',
  'bidder.nav.myBids': 'Tekliflerim',
  'bidder.nav.membership': 'Üyelik',
  'bidder.nav.login': 'Giriş',
  'bidder.nav.register': 'Kayıt Ol',
  'bidder.nav.logout': 'Çıkış',
  'bidder.lang.label': 'Dil',
  // Corporate surfaces mirrored from the main brand site (see apps/web-bidder/src/lib/site.ts).
  'bidder.site.home': 'Anasayfa',
  'bidder.site.about': 'Hakkımızda',
  'bidder.site.services': 'Hizmetlerimiz',
  'bidder.site.contact': 'İletişim',
  'bidder.site.legal': 'KVKK ve Yasal Metinler',
  // footer
  'bidder.footer.tagline':
    'Türkiye genelinde 81 ilde araç operasyon yönetimi, filo yönetimi, alım-satım süreçleri ve hukuk müşavirliği alanlarında bütünleşik çözümler sunuyoruz.',
  'bidder.footer.quickLinks': 'Hızlı Erişim',
  'bidder.footer.corporate': 'Kurumsal',
  'bidder.footer.contact': 'Bize Ulaşın',
  'bidder.footer.follow': 'Bizi takip edin',
  'bidder.footer.rights': '© {year} {company} · Tüm Hakları Saklıdır',
  'bidder.header.contactBar': 'İletişim bilgileri',
  // push notifications
  'bidder.notif.enable': 'Bildirimleri aç',
  'bidder.notif.disable': 'Bildirimleri kapat',
  'bidder.notif.enabledToast': 'Bildirimler açıldı',
  'bidder.notif.disabledToast': 'Bildirimler kapatıldı',
  'bidder.notif.blocked': 'Bildirimler tarayıcı ayarlarından engellenmiş.',
  'bidder.notif.failed': 'Bildirimler açılamadı',
  // showcase
  'bidder.showcase.title': 'Açık Artırmalar',
  'bidder.showcase.empty': 'Şu anda gösterilecek açık artırma yok.',
  'bidder.showcase.subline': '{n} açık artırma · canlı takip',
  'bidder.showcase.lots': '{n} araç',
  'bidder.showcase.view': 'Araçları gör',
  // The gate where the auction list sits for a logged-out visitor (ADR 0027).
  'bidder.showcase.membersOnly': 'Açık artırmalar üyelere özeldir',
  'bidder.showcase.loginPrompt':
    'Açık artırmaları, araçları ve sonuçlarını görmek için giriş yapın.',
  'bidder.showcase.remaining': 'Kalan',
  'bidder.showcase.startsIn': 'Başlıyor',
  'bidder.showcase.stats.salesHeld': 'Yapılan ihale',
  'bidder.showcase.stats.vehiclesSold': 'Satılan araç',
  // login
  'bidder.login.title': 'Giriş',
  'bidder.login.noAccount': 'Hesabınız yok mu?',
  'bidder.login.registerLink': 'Kayıt olun',
  // register
  'bidder.register.title': 'Kayıt Ol',
  'bidder.register.first': 'Ad',
  'bidder.register.last': 'Soyad',
  'bidder.register.email': 'E-posta',
  'bidder.register.password': 'Parola',
  'bidder.register.mobile': 'Telefon (opsiyonel)',
  'bidder.register.submit': 'Hesap Oluştur',
  'bidder.register.submitting': 'Oluşturuluyor…',
  'bidder.register.failed': 'Kayıt başarısız',
  'bidder.register.consentIntro': 'Devam etmek için aşağıdaki metinleri onaylayın:',
  'bidder.register.section.identity': 'Kimlik',
  'bidder.register.section.contact': 'İletişim',
  'bidder.register.section.password': 'Parola',
  // Each consent sentence is split so the **document name itself** is the link. The pieces
  // differ by language — Turkish suffixes the name, English prefixes an article — which is why
  // this is three keys and not one string with a placeholder.
  'bidder.register.consentAydinlatmaPre': '',
  'bidder.register.consentAydinlatmaDoc': 'KVKK Aydınlatma Metni',
  'bidder.register.consentAydinlatmaPost': '’ni okudum.',
  'bidder.register.consentAcikRizaPre': '',
  'bidder.register.consentAcikRizaDoc': 'Açık Rıza Metni',
  'bidder.register.consentAcikRizaPost': '’ni kabul ediyorum.',
  'bidder.register.consentRequired': 'Her iki metni de onaylamanız gerekir.',
  // Approval gate (M15) — registration creates an application, not a session.
  'bidder.register.pendingTitle': 'Başvurunuz alındı',
  'bidder.register.pendingBody':
    'Üyelik başvurunuz incelenmek üzere iletildi. Onaylandığında e-posta ile bilgilendirileceksiniz; o ana kadar giriş yapamazsınız.',
  'bidder.login.pending': 'Üyelik başvurunuz henüz incelenmedi. Onaylandığında bilgilendirileceksiniz.',
  'bidder.login.rejected': 'Üyelik başvurunuz onaylanmadı. Ayrıntı için bizimle iletişime geçin.',
  'bidder.register.haveAccount': 'Zaten hesabınız var mı?',
  'bidder.register.loginLink': 'Giriş yapın',
  // auction detail
  'bidder.detail.back': '← Açık artırmalara dön',
  'bidder.detail.startingPrice': 'Başlangıç',
  'bidder.detail.currentBid': 'Güncel teklif',
  'bidder.detail.bidCount': 'Teklif',
  'bidder.detail.remaining': 'Kalan süre',
  'bidder.detail.guaranteed': 'Kesin satış',
  'bidder.detail.reserveMet': 'Fiyat hedefine ulaşıldı',
  'bidder.detail.bid': '+1 Teklif ver',
  'bidder.detail.jump': 'Hedefe yükselt',
  'bidder.detail.early': 'Erken teklif',
  'bidder.detail.deposit': 'Depozito yatır',
  'bidder.detail.depositNeeded': 'Teklif için depozito gerekir',
  'bidder.detail.goldBypass': 'Üyeliğiniz depozito gerektirmez',
  'bidder.detail.joinConf': 'Konferansa katıl →',
  'bidder.detail.closed': 'Kapandı',
  'bidder.detail.sold': 'Satıldı',
  'bidder.detail.won': 'Kazandınız!',
  'bidder.detail.outbid': 'Teklifiniz geçildi',
  'bidder.detail.actionFailed': 'İşlem başarısız',
  'bidder.detail.jumpPrompt': 'Hedef tutar ({currency}):',
  'bidder.detail.bidCountShort': '{n} teklif',
  'bidder.detail.view': 'İncele',
  'bidder.detail.remainingShort': '{time} kaldı',
  'bidder.detail.mustExceedTop': 'Mevcut teklifin üzerinde olmalı',
  'bidder.detail.earlyInvalid': 'Geçerli bir adım sayısı girin',
  'bidder.detail.earlyPrompt': 'Kaç adım (erken teklif)?',
  'bidder.detail.loginToBid': 'Teklif vermek için giriş yapın',
  // Bid confirmation (M19, audit Y-08). A bid was placed straight from the button press with
  // nothing said about what it commits the bidder to.
  'bidder.detail.confirmTitle': 'Teklifi onaylayın',
  'bidder.detail.confirmAmount': 'Teklifiniz: {amount}',
  'bidder.detail.binding': 'Verilen teklif bağlayıcıdır ve geri alınamaz.',
  'bidder.detail.jumpRounding':
    'Girdiğiniz tutar, artış adımına uyan en yakın alt tutara yuvarlanır.',
  'bidder.detail.confirmBid': 'Teklifi ver',
  'bidder.detail.priceMoved':
    'Siz onaylarken fiyat değişti. Güncel tutarı görüp yeniden deneyin.',
  // deposit dialog
  'bidder.deposit.title': 'Depozito',
  'bidder.deposit.holder': 'Kart sahibi',
  'bidder.deposit.number': 'Kart numarası',
  'bidder.deposit.expiry': 'SKT (AA/YY)',
  'bidder.deposit.cvv': 'CVV',
  'bidder.deposit.submit': 'Öde',
  // my bids
  'bidder.myBids.title': 'Tekliflerim',
  'bidder.myBids.empty': 'Henüz teklifiniz yok.',
  'bidder.myBids.sale': 'Açık artırma',
  'bidder.myBids.plate': 'Plaka',
  'bidder.myBids.amount': 'Tutar',
  'bidder.myBids.kind': 'Tür',
  'bidder.myBids.time': 'Zaman',
  'bidder.myBids.perPage': 'Sayfada',
  'bidder.myBids.viewVehicle': 'Aracı gör',
  'bidder.myBids.subline': '{n} teklif · en yeniden eskiye',
  // dashboard (/me) — the bidder's own panel
  'bidder.dashboard.title': 'Panelim',
  // Split into two single-count halves (M19): English needs "1 auction" but "2 auctions", and
  // the plural machinery keys on one `n` per message, so a message carrying two counts can
  // never be right in both halves at once. The caller joins them with the separator.
  'bidder.dashboard.sublineSales': '{n} açık artırma',
  'bidder.dashboard.sublineBids': '{n} teklif',
  'bidder.dashboard.empty': 'Henüz bir açık artırmaya katılmadınız.',
  'bidder.dashboard.section.participation': 'Katılımım',
  'bidder.dashboard.sales': 'Katıldığım açık artırma',
  'bidder.dashboard.lots': 'Teklif verdiğim araç',
  'bidder.dashboard.bids': 'Verdiğim teklif',
  'bidder.dashboard.section.results': 'Sonuçlarım',
  'bidder.dashboard.won': 'Kazandığım',
  'bidder.dashboard.lost': 'Kaybettiğim',
  'bidder.dashboard.pending': 'Devam eden',
  'bidder.dashboard.pendingHint': 'Sonucu belli olmayan araçlar',
  'bidder.dashboard.leading': 'Önde olduğum',
  'bidder.dashboard.leadingHint': 'Şu an en yüksek teklif sizin',
  'bidder.dashboard.recent': 'Son teklifleriniz',
  'bidder.dashboard.viewAll': 'Tümünü gör',
  // membership
  'bidder.membership.title': 'Üyelik Paketleri',
  'bidder.membership.subline': '{n} paket',
  'bidder.membership.empty': 'Şu anda satın alınabilir paket yok.',
  'bidder.membership.buy': 'Satın al',
  'bidder.membership.alreadyMember': 'Zaten üyesiniz',
  'bidder.membership.purchaseFailed': 'Satın alma başarısız',
  // payment result
  'bidder.payment.successTitle': 'Ödeme başarılı',
  'bidder.payment.successBody': 'İşleminiz tamamlandı.',
  'bidder.payment.failTitle': 'Ödeme başarısız',
  'bidder.payment.failBody': 'İşleminiz tamamlanamadı.',
  'bidder.payment.pendingTitle': 'Ödeme işleniyor',
  'bidder.payment.pendingBody': 'İşleminiz doğrulanıyor, lütfen bekleyin.',
  'bidder.payment.backHome': 'Açık artırmalara dön',
  // native push copy (M8, ADR 0021) — resolved server-side per recipient locale via `tf`
  'push.outbid.title': 'Teklifiniz geçildi',
  'push.outbid.body': 'Takip ettiğiniz araçta daha yüksek bir teklif verildi.',
  'push.youWon.title': 'Tebrikler! İhaleyi kazandınız',
  'push.youWon.body': '{plate} sizin oldu.',
  'push.youWon.bodyNoPlate': 'Bir aracı kazandınız.',
  'push.digest.title': 'Bugünün İhaleleri',
  'push.digest.body': '{count} açık artırma bugün sizi bekliyor.',
  // --- M11 vehicle detail -------------------------------------------------
  // Turkish labels come from the LEGACY locale file (`service/locales/tr.json` in the parent
  // repo) so the wording matches what operators and bidders already know; English is authored
  // (the legacy en.json was never actually translated). Enum KEYS are the legacy identifiers
  // verbatim, so ported data and the ingestion API need no translation layer.
  'nav.vehicles': 'Araçlar',
  'vehicles.title': 'Araçlar',
  'vehicles.new': 'Yeni Araç',
  'vehicles.empty': 'Henüz araç yok',
  'vehicles.search.placeholder': 'Plaka, marka, model…',
  'vehicles.count': '{n} araç',
  'vehicles.col.photo': 'Foto',
  'vehicles.col.plate': 'Plaka',
  'vehicles.col.vehicle': 'Araç',
  'vehicles.col.yearKm': 'Yıl / Km',
  'vehicles.col.price': 'Fiyat',
  'vehicles.col.status': 'Durum',
  'vehicles.col.auction': 'İhale',
  'vehicles.status.available': 'Müsait',
  'vehicles.status.committed': 'İhalede',
  // Pool filters
  'vehicles.filter.search': 'Ara',
  'vehicles.filter.auction': 'İhale',
  'vehicles.filter.auction.none': 'Hiç ihaleye girmemiş',
  'vehicles.filter.status': 'Durum',
  'vehicles.filter.firm': 'Firma',
  'vehicles.filter.firm.none': 'Firması yok',
  'vehicles.filter.year': 'Model yılı',
  'vehicles.filter.yearMin': 'En eski model yılı',
  'vehicles.filter.yearMax': 'En yeni model yılı',
  'vehicles.filter.yearInvalid': '1900–2999 arasında dört haneli bir yıl girin',
  'vehicles.filter.yearRangeInvalid': 'Başlangıç yılı bitiş yılından sonra olamaz',
  'vehicles.filter.active': '{n} filtre açık',
  'vehicles.filter.clear': 'Filtreleri temizle',
  'vehicles.emptyFiltered': 'Bu filtrelere uyan araç yok',
  'vehicles.emptyFilteredHint': 'Filtreleri temizleyip tekrar deneyin.',
  'vehicles.created': 'Araç oluşturuldu',
  'vehicles.updated': 'Araç güncellendi',
  'vehicles.deleted': 'Araç silindi',
  'vehicles.deleteTitle': 'Aracı sil',
  'vehicles.deleteBody': '{plate} plakalı araç kalıcı olarak silinecek. Bu işlem geri alınamaz.',
  'vehicles.saveFailed': 'Araç kaydedilemedi',
  'vehicles.deleteFailed': 'Araç silinemedi',
  'vehicles.notFound': 'Araç bulunamadı',
  'vehicles.unsavedTitle': 'Kaydedilmemiş değişiklikler',
  'vehicles.unsavedBody': 'Bu sayfadan ayrılırsanız değişiklikleriniz kaybolur.',

  // Form sections
  'vehicles.section.identity': 'Kimlik',
  'vehicles.section.technical': 'Teknik',
  'vehicles.section.equipment': 'Donanım',
  'vehicles.section.condition': 'Durum',
  'vehicles.section.expertise': 'Ekspertiz',
  'vehicles.section.description': 'Açıklama',
  'vehicles.section.pricing': 'Fiyat',
  'vehicles.section.media': 'Görseller',

  // Fields
  'vehicles.field.plate': 'Plaka',
  'vehicles.field.oldPlate': 'Eski plaka',
  'vehicles.field.chassis': 'Şasi no',
  'vehicles.field.motor': 'Motor no',
  'vehicles.field.code': 'Araç kodu',
  'vehicles.field.vehicleType': 'Araç tipi',
  'vehicles.field.disability': 'Engelli aracı',
  'vehicles.field.brand': 'Marka',
  'vehicles.field.series': 'Seri',
  'vehicles.field.modelName': 'Model',
  'vehicles.field.year': 'Yıl',
  'vehicles.field.color': 'Renk',
  'vehicles.field.km': 'Km',
  'vehicles.field.condition': 'Durumu',
  'vehicles.field.fuel': 'Yakıt',
  'vehicles.field.gear': 'Vites',
  'vehicles.field.bodyType': 'Kasa tipi',
  'vehicles.field.engineCapacity': 'Motor hacmi (cc)',
  'vehicles.field.enginePower': 'Motor gücü (hp)',
  'vehicles.field.pledgeBank': 'Rehin bankası',
  'vehicles.field.fuelStatus': 'Yakıt seviyesi (%)',
  'vehicles.field.insuranceValidUntil': 'Sigorta bitiş',
  'vehicles.field.inspectionValidUntil': 'Muayene bitiş',
  'vehicles.field.description': 'İhale açıklaması',
  'vehicles.field.startingPrice': 'Başlangıç fiyatı',
  'vehicles.field.reserve': 'Gizli rezerv',
  'vehicles.field.increment': 'Artış miktarı',
  'vehicles.field.company': 'Firma',
  'vehicles.field.clientRef': 'Müşteri referansı',
  'vehicles.hint.modelName': 'Marka listesi hazır; model serbest metindir.',
  'vehicles.hint.reserve': 'Teklif verenlere hiçbir zaman gösterilmez.',
  'vehicles.priceLockedTitle': 'Fiyat alanları kilitli',
  'vehicles.priceLockedBody': 'Araç planlanmış veya devam eden bir lotta. Fiyat alanları ihale bitene kadar değiştirilemez; diğer bilgiler güncellenebilir.',

  // Vehicle type
  'vehicleType.automobile': 'Binek',
  'vehicleType.suv': 'SUV',
  'vehicleType.commercial': 'Ticari',
  'vehicleType.heavy-commercial': 'Ağır Ticari',
  'vehicleType.motorcycles': 'Motosiklet',
  'vehicleType.minivans': 'Minibüs',
  'vehicleType.bus': 'Otobüs',
  'vehicleType.truck': 'Kamyon',
  'vehicleType.damaged': 'Hasarlı',
  'vehicleType.earth-movers': 'İş Makinesi',

  // Fuel
  'fuel.gasoline': 'Benzin',
  'fuel.gasoline-lpg': 'Benzin ve LPG',
  'fuel.diesel': 'Dizel',
  'fuel.lpg': 'LPG',
  'fuel.hybrid': 'Hibrit',
  'fuel.electric': 'Elektrik',

  // Gear
  'gear.automatic': 'Otomatik',
  'gear.semi-automatic': 'Yarı otomatik',
  'gear.tiptronic': 'Tiptronic',
  'gear.manual': 'Manuel',
  'gear.cvt': 'CVT',
  'gear.s-tronic': 'S-tronic',

  // Body type (keys are the legacy English identifiers; only the labels are localized)
  'bodyType.Cabrio': 'Cabrio',
  'bodyType.Coupe': 'Coupe',
  'bodyType.Hatchback 3-door': 'Hatchback 3 kapı',
  'bodyType.Hatchback 5-door': 'Hatchback 5 kapı',
  'bodyType.Sedan': 'Sedan',
  'bodyType.Station Wagon': 'Station Wagon',
  'bodyType.Crossover': 'Crossover',
  'bodyType.MPV': 'MPV',
  'bodyType.Roadster': 'Roadster',
  'bodyType.Off-road': 'Arazi',

  // Condition
  'vehicleCondition.new': 'Sıfır',
  'vehicleCondition.used': 'İkinci el',
  'vehicleCondition.free-certified': 'Garantili',

  // Expertise — the 14 body parts
  'expertise.title': 'Boya ve Değişen Kaporta Bilgisi',
  'expertise.disclaimer': 'Boya ve değişen kaporta bilgileri referans amaçlı olup, firmamız sorumluluğu dışındadır.',
  'expertise.original': 'Orijinal',
  'expertise.originalBadge': 'Orijinal',
  'expertise.notAssessed': 'Değerlendirilmedi',
  'expertise.notAssessedHint': 'Boş bırakılan parça "değerlendirilmedi" sayılır; araç ancak 14 parçanın tamamı orijinal işaretlendiğinde "Orijinal" rozetini alır.',
  'expertise.part.frontBumper': 'Ön tampon',
  'expertise.part.frontHood': 'Motor kaputu',
  'expertise.part.roof': 'Tavan',
  'expertise.part.frontRightMudguard': 'Ön sağ çamurluk',
  'expertise.part.frontRightDoor': 'Ön sağ kapı',
  'expertise.part.rearRightDoor': 'Arka sağ kapı',
  'expertise.part.rearRightMudguard': 'Arka sağ çamurluk',
  'expertise.part.frontLeftMudguard': 'Ön sol çamurluk',
  'expertise.part.frontLeftDoor': 'Ön sol kapı',
  'expertise.part.rearLeftDoor': 'Arka sol kapı',
  'expertise.part.rearLeftMudguard': 'Arka sol çamurluk',
  'expertise.part.rearHood': 'Bagaj',
  'expertise.part.rearBumper': 'Arka tampon',
  'expertise.part.spareTire': 'Stepne',
  'expertise.state.original': 'Orijinal',
  'expertise.state.changed': 'Değiştirildi',
  'expertise.state.painted': 'Boyalı',
  'expertise.state.local-painted': 'Lokal boyalı',
  'expertise.state.crushed': 'Ezik',
  'expertise.state.sketched': 'Çizik',
  'expertise.state.registered': 'Şasi işlemli',

  // Equipment groups
  'equipmentKind.internal': 'İç donanım',
  'equipmentKind.external': 'Dış donanım',
  'equipmentKind.security': 'Güvenlik',
  'equipmentKind.multimedia': 'Multimedya',
  'equipmentKind.other': 'Diğer',

  // Hand-over checklist (the legacy `has` block)
  'checklist.title': 'Araçla teslim edilenler',
  'checklist.hint': 'İşaretlenmeyen madde "kayıt yok" demektir; "yok" değil.',
  'checklist.warranty': 'Garanti',
  'checklist.key': 'Anahtar',
  'checklist.spareKey': 'Yedek anahtar',
  'checklist.license': 'Ruhsat',
  'checklist.spareTyre': 'Stepne',
  'checklist.lifter': 'Kriko',
  'checklist.trafficSet': 'Trafik seti',
  'checklist.chain': 'Zincir',
  'checklist.floorMat': 'Paspas',
  'checklist.winterTire': 'Kış lastiği',
  'checklist.jackLever': 'Bijon anahtarı',
  'checklist.extinguisher': 'Yangın söndürücü',
  'checklist.wheelScrew': 'Bijon',
  'checklist.serviceManualsWarranty': 'Servis kitapçığı',
  'checklist.doublePlateSealControl': 'Çift plaka mühür kontrolü',
  'checklist.firstAidKit': 'İlk yardım çantası',
  'checklist.extraTireSet': 'İlave lastik takımı',
  // M11 media manager (redesign-vehicle.md §4)
  'media.add': 'Görsel ekle',
  'media.kind': 'Görsel türü',
  'media.kind.photo': 'Fotoğraf',
  'media.kind.damagePhoto': 'Hasar fotoğrafı',
  'media.kind.video': 'Video',
  'media.kind.damageVideo': 'Hasar videosu',
  'media.cover': 'Kapak',
  'media.membersOnly': 'Sadece üyeler',
  'media.makeCover': 'Kapak yap',
  'media.showToPublic': 'Herkese aç',
  'media.hideFromPublic': 'Herkese açık (kapatmak için tıklayın)',
  'media.moveLeft': 'Sola taşı',
  'media.moveRight': 'Sağa taşı',
  'media.open': 'Büyük görüntüle',
  'media.viewerCounter': '{i} / {n}',
  'media.dropHint': 'Dosyaları buraya sürükleyin veya "Görsel ekle"ye tıklayın',
  'media.uploading': 'Yükleniyor…',
  'media.uploaded': '{n} görsel yüklendi',
  'media.updateFailed': 'Görsel güncellenemedi',
  'media.deleteFailed': 'Görsel silinemedi',
  'media.deleteTitle': 'Görseli sil',
  'media.deleteBody': 'Görsel kalıcı olarak silinecek. Bu işlem geri alınamaz.',
  'media.error.decode': 'Dosya okunamadı',
  'media.error.unencodable': 'Bu format küçültülemiyor; daha küçük bir dosya yükleyin',
  'media.error.videoType': 'Yalnızca MP4 veya WebM video yüklenebilir',
  'media.error.generic': 'Yüklenemedi',
  'media.empty': 'Henüz görsel yok',
  // Submissions moderation detail (M11)
  'submissions.photoCount': '{n} görsel',
  'submissions.noPhotos': 'Görsel yok',
  'submissions.mediaReport': 'Görsel aktarım raporu',
  'submissions.mediaReportFailed': '{n} görsel aktarılamadı',
  // M11 bidder vehicle page + masked prices (redesign-vehicle.md §7/§8)
  'bidder.masked.title': 'Fiyatları görmek için giriş yapın',
  'bidder.masked.loginToBid': 'Teklif vermek için giriş yapın',
  'bidder.vehicle.backToAuction': '← İhaleye dön',
  'bidder.vehicle.goToRoom': 'İhale odasına git',
  'bidder.vehicle.noPhotos': 'Bu araç için görsel eklenmemiş',
  'bidder.vehicle.openPhoto': 'Fotoğrafı büyüt',

  // Admin help centre (`/help`) — CHROME ONLY. The glossary/handbook/FAQ body text lives in
  // `apps/web-admin/src/content/help/*`, not here: this bundle also ships to web-bidder and
  // the Worker, and the admin app is tr-only, so several thousand words of operator prose have
  // no business in it. See the header of `content/help/types.ts`.
  'nav.help': 'Yardım',
  // Route permission guard (M19, audit O-06) — admin chrome, tr-only like the rest of it.
  'forbidden.title': 'Bu sayfaya erişiminiz yok',
  'forbidden.body':
    'Bu bölüm için yetkiniz bulunmuyor. Erişmeniz gerekiyorsa bir yönetici ile iletişime geçin.',
  'forbidden.home': 'Genel bakışa dön',
  'help.title': 'Yardım Merkezi',
  'help.subline': 'Terimler, adım adım kılavuz, sık sorulanlar ve iş ortağı rehberi.',
  'help.tab.glossary': 'Sözlük',
  'help.tab.handbook': 'Kılavuz',
  'help.tab.faq': 'Sık Sorulanlar',
  'help.tab.partner': 'İş Ortağı Rehberi',
  // Partner-guide chrome: the requirement table and the business/technical layer divider.
  'help.field': 'Alan',
  'help.fieldMeaning': 'Anlamı',
  'help.requirement': 'Önem',
  'help.req.required': 'Zorunlu',
  'help.req.recommended': 'Önemli',
  'help.req.optional': 'İsteğe bağlı',
  'help.layer.business': 'Hazırlık ve süreç',
  'help.layer.technical': 'Teknik entegrasyon',
  'help.layer.technicalHint': 'Bu bölüm bağlantıyı kuracak yazılımcı içindir.',
  'help.search': 'Yardımda ara…',
  'help.noResults': 'Aramanızla eşleşen bir başlık yok.',
  'help.matchCount': '{n} sonuç',
  'help.otherTabMatches': 'Diğer sekmelerde {n} sonuç daha var.',
  'help.when': 'Ne zaman',
  'help.perm': 'Yetki',
  'help.warnings': 'Dikkat',
  'help.seeAlso': 'İlgili',
} as const;

export type MessageKey = keyof typeof tr;
type Dict = Record<MessageKey, string>;

/**
 * The CLDR plural categories `Intl.PluralRules` can return. A dictionary may carry
 * `<key>_<category>` variants beside a base key, and {@link tf} prefers the matching one when
 * the message is given a numeric `n` (see `pluralKey`).
 *
 * Variants live in the locale that needs them rather than in `tr`: Turkish has a single
 * category, so mirroring every English `_one`/`_other` pair into `tr` would add two identical
 * lines per counted message for no reader. The template type still ties each variant to a real
 * `MessageKey`, so a typo in the base half is a compile error exactly as before.
 */
type PluralCategory = Intl.LDMLPluralRule;
type PluralKey = `${MessageKey}_${PluralCategory}`;
/** A locale's messages: base keys, plus optional plural variants of them. */
type LocaleDict = Partial<Dict> & Partial<Record<PluralKey, string>>;

/** English overrides; missing keys fall back to `tr`. */
const en: LocaleDict = {
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.actions': 'Actions',
  'common.confirm': 'Confirm',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.close': 'Close',
  'common.skipToContent': 'Skip to content',
  'common.none': '—',
  'common.logout': 'Log out',
  'common.loading': 'Loading…',
  'common.error': 'Something went wrong',
  'common.rateLimited': 'Too many attempts. Please try again in a little while.',
  'common.turnstileFailed': 'Security check failed. Please try again.',
  'common.errorBoundary.title': 'Something went wrong',
  'common.errorBoundary.message': 'An unexpected error occurred. Try reloading the page.',
  'common.errorBoundary.reload': 'Reload page',
  'common.errorBoundary.requestId': 'Support reference',
  'email.depositConfirmed.subject': 'Your deposit is confirmed',
  'email.depositRefunded.subject': 'Your deposit has been refunded',
  'email.depositReceivedAdmin.subject': 'New deposit received',
  'email.registrationReceivedAdmin.subject': 'New membership application',
  'email.accountApproved.subject': 'Your account has been approved',
  'email.passwordReset.subject': 'Reset your password',
  'email.membershipActivated.subject': 'Your membership is active',
  'email.youWon.subject': 'Congratulations, you won the auction',
  'email.lotUnsold.subject': 'Vehicle unsold',
  // Role labels (en) — full set so a membership tier configured with *any* role localizes in the
  // activation email (and the bidder/admin UI), never falling back to the Turkish label.
  'role.ADMIN': 'Administrator',
  'role.DEVELOPER': 'Developer',
  'role.EDITOR': 'Editor',
  'role.LOGISTIC_EDITOR': 'Logistics Editor',
  'role.WEB_EDITOR': 'Web Editor',
  'role.FIRM': 'Firm',
  'role.FIRM_AUCTION': 'Firm (Auction)',
  'role.FIRM_USER': 'Firm (User)',
  'role.FIRM_VENDOR': 'Firm (Vendor)',
  'role.ANONYMOUS': 'Anonymous',
  'role.CLIENT': 'Client',
  'role.GOLDBIDDER': 'Gold Member',
  'role.BIDDER': 'Bidder',
  'role.USER': 'User',
  'app.title': '{brand} Admin',
  'app.nav.title': 'Admin',
  'nav.dashboard': 'Overview',
  'nav.sales': 'Auctions',
  'nav.submissions': 'Submissions',
  'nav.users': 'Users',
  'nav.firms': 'Firms',
  'nav.audit': 'Audit',
  'nav.config': 'Settings',
  'nav.toggle': 'Collapse/expand menu',
  'theme.switchToDark': 'Dark theme',
  'theme.switchToLight': 'Light theme',
  'dashboard.title': 'Overview',
  'dashboard.section.sales': 'Auctions',
  'dashboard.section.lots': 'Vehicles',
  'dashboard.section.sold': 'Sold value',
  'dashboard.sales.active': 'Active',
  'dashboard.sales.activeHint': 'Live, paused or published',
  'dashboard.sales.live': 'Live',
  'dashboard.sales.published': 'Published',
  'dashboard.sales.paused': 'Paused',
  'dashboard.sales.draft': 'Draft',
  'dashboard.sales.passive': 'Inactive',
  'dashboard.sales.passiveHint': 'Completed or cancelled',
  'dashboard.sales.closed': 'Completed',
  'dashboard.sales.cancelled': 'Cancelled',
  'dashboard.lots.total': 'Total vehicles',
  'dashboard.lots.open': 'Open',
  'dashboard.lots.scheduled': 'Queued',
  'dashboard.lots.sold': 'Sold',
  'dashboard.lots.unsold': 'Unsold',
  'dashboard.sold.count': '{n} vehicles sold',
  'dashboard.sold.empty': 'Nothing sold yet.',
  'dashboard.live.title': 'Auctions running now',
  'dashboard.live.empty': 'No auction is running.',
  'dashboard.viewAll': 'All auctions \u2192',
  // M10 — bidder profile / refund / audit viewer / list search (admin surface).
  'users.detail.notFound': 'User not found.',
  'users.detail.viewAudit': 'View audit trail',
  'users.detail.deposits': 'Deposits',
  'users.detail.bids': 'Bid history',
  'users.detail.noDeposits': 'No deposits.',
  'users.detail.noBids': 'No bids.',
  'users.detail.col.sale': 'Sale',
  'users.detail.col.plate': 'Plate',
  'users.detail.col.amount': 'Amount',
  'users.detail.col.state': 'Status',
  'users.detail.col.date': 'Date',
  'users.detail.col.kind': 'Kind',
  'users.detail.col.lotState': 'Lot status',
  'depositState.PENDING': 'Pending',
  'depositState.HELD': 'Held',
  'depositState.CAPTURED': 'Captured',
  'depositState.REFUNDED': 'Refunded',
  'depositState.VOIDED': 'Voided',
  'deposits.refund.action': 'Refund',
  'deposits.refund.confirm': 'Refund the {amount} deposit on sale {sale}?',
  'deposits.refund.voided': 'Deposit voided (same-day)',
  'deposits.refund.refunded': 'Deposit refunded',
  'deposits.refund.failed': 'Refund failed',
  'deposits.refund.blockedTopBidder':
    'User holds the top bid on this lot — refund after the lot closes.',
  'deposits.refund.declined': 'The bank declined the refund.',
  'deposits.refund.ambiguous':
    'No bank answer — the deposit is locked; reconcile via the bank portal.',
  'deposits.refund.inProgress': 'A refund for this deposit is already in progress.',
  'deposits.refund.notRefundable': 'The deposit is not refundable in its current state.',
  'deposits.orderIdLabel': 'Bank order id',
  'deposits.state.reconcile': 'Reconciliation pending',
  'deposits.state.reconcileHint':
    'The bank call failed mid-refund — reconcile via the bank portal.',
  'deposits.reconcile.action': 'Reconcile',
  'deposits.reconcile.confirm': 'Query the bank order status and reconcile this deposit?',
  'deposits.reconcile.confirmedVoided': 'The bank confirmed the void — payment finalized as voided.',
  'deposits.reconcile.confirmedRefunded': 'The bank confirmed the refund — payment finalized as refunded.',
  'deposits.reconcile.reverted':
    'No refund at the bank — the deposit is HELD again; you can retry the refund.',
  'deposits.reconcile.ambiguous':
    'The bank inquiry was inconclusive — nothing changed; check the bank portal.',
  'deposits.reconcile.notPending': 'This deposit does not need reconciliation.',
  'deposits.reconcile.inProgress':
    'A refund is currently in flight for this deposit — retry once it settles.',
  'deposits.reconcile.cooldown':
    'The bank still shows the charge standing — reconcile again after 10 minutes to confirm.',
  'deposits.reconcile.failed': 'Reconciliation failed',

  // payment attempts + stuck-PRE3D reconcile (post-M10, ADR 0011 amendment)
  'users.detail.payments': 'Payments',
  'users.detail.noPayments': 'No payments.',
  'users.detail.col.order': 'Order id',
  'paymentState.WAITING': 'Waiting',
  'paymentState.PRE3D': '3DS in progress',
  'paymentState.SUCCESSFUL': 'Successful',
  'paymentState.FAIL': 'Failed',
  'paymentState.VOIDED': 'Voided',
  'paymentState.REFUNDED': 'Refunded',
  'payments.kind.deposit': 'Deposit',
  'payments.kind.membership': 'Membership',
  'payments.state.stuck': 'Stuck (3DS callback lost)',
  'payments.state.stuckHint':
    'The 3DS callback never arrived — the bank may have charged; reconciliation is required. The user cannot start a new payment until this row is resolved.',
  'payments.reconcile.action': 'Reconcile',
  'payments.reconcile.confirm': 'Query the bank order status and reconcile this payment?',
  'payments.reconcile.confirmed':
    'The bank confirmed the charge — payment finalized and the entitlement applied.',
  'payments.reconcile.returned':
    'The bank returned the money — payment closed; the user can retry.',
  'payments.reconcile.ambiguous':
    'The bank inquiry was inconclusive — nothing changed; check the bank portal.',
  'payments.reconcile.notPending': 'This payment does not need reconciliation.',
  'payments.reconcile.failed': 'Reconciliation failed',
  'payments.markFailed.action': 'Mark failed',
  'payments.markFailed.confirm':
    'WARNING: Use only if the order was NEVER charged at the bank (verify in the bank portal). The payment will be closed as FAIL and the user can retry. If a charge stands, this can lead to a double charge. Continue?',
  'payments.markFailed.done': 'Payment closed as failed — the user can retry.',
  'audit.filter.from': 'From',
  'audit.filter.to': 'To',
  'audit.title': 'Audit Log',
  'audit.filter.entityType': 'Entity type',
  'audit.filter.entityId': 'Entity id',
  'audit.filter.actorId': 'Actor id',
  'audit.filter.actorCompany': 'Firm (API)',
  'audit.filter.action': 'Action',
  'audit.col.time': 'Time',
  'audit.col.actor': 'Actor',
  'audit.col.entity': 'Entity',
  'audit.col.action': 'Action',
  'audit.col.details': 'Details',
  'audit.expand': 'Show details',
  'audit.redacted': '—',
  'audit.systemActor': 'system',
  'audit.firmActor': '{name} (firm key)',
  'audit.empty': 'No entries found.',
  'audit.filter.clear': 'Clear',
  'audit.filter.active': '{n} filters active',
  'audit.detailsTitle': 'Entry detail',
  'audit.before': 'Before',
  'audit.after': 'After',
  'audit.entity.sale': 'Auction',
  'audit.entity.lot': 'Lot',
  'audit.entity.bid': 'Bid',
  'audit.entity.deposit': 'Deposit',
  'audit.entity.payment': 'Payment',
  'audit.entity.user': 'User',
  'audit.entity.config': 'Setting',
  'audit.entity.vehicle': 'Vehicle',
  'audit.entity.submission': 'Submission',
  'audit.entity.company': 'Firm',
  'audit.entity.api-key': 'API key',
  'audit.action.create': 'Created',
  'audit.action.update': 'Updated',
  'audit.action.delete': 'Deleted',
  'audit.action.approve': 'Approved',
  'audit.action.reject': 'Rejected',
  'audit.action.archive': 'Archived',
  'audit.action.restore': 'Restored',
  'audit.action.register': 'Registered',
  'audit.action.membership-approved': 'Membership approved',
  'audit.action.membership-rejected': 'Membership rejected',
  'audit.action.initiate': 'Initiated',
  'audit.action.grant-role': 'Role granted',
  'audit.action.bid-placed': 'Bid placed',
  'audit.action.set-reserve': 'Reserve set',
  'audit.action.take-control': 'Control taken',
  'audit.action.pause': 'Paused',
  'audit.action.resume': 'Resumed',
  'audit.action.re-add': 'Re-added',
  'audit.action.settlement-completed': 'Settlement completed',
  'audit.action.media-add': 'Media added',
  'audit.action.media-update': 'Media updated',
  'audit.action.media-delete': 'Media deleted',
  'audit.action.media-upload': 'Media uploaded',
  'audit.action.provision-conference': 'Conference provisioned',
  'audit.action.teardown-conference': 'Conference torn down',
  'audit.action.teardown-conference-failed': 'Conference teardown failed',
  'audit.action.recording-capture-exhausted': 'Recording capture exhausted',
  'audit.action.success': 'Payment succeeded',
  'audit.action.fail': 'Payment failed',
  'audit.action.revive': 'Revived',
  'audit.action.duplicate-payment': 'Duplicate payment',
  'audit.action.refund': 'Refunded',
  'audit.action.refund-ambiguous': 'Refund ambiguous',
  'audit.action.refund-declined': 'Refund declined',
  'audit.action.refund-topbidder-conflict': 'Refund — top-bidder conflict',
  'audit.action.void': 'Void',
  'audit.action.credit': 'Refund (credit)',
  'audit.action.reconcile-ambiguous': 'Reconcile ambiguous',
  'audit.action.reconcile-attempt-failed': 'Reconcile attempt failed',
  'audit.action.reconcile-charged-pending': 'Reconcile — awaiting charge confirmation',
  'audit.action.reconcile-confirmed': 'Reconcile confirmed',
  'audit.action.reconcile-returned': 'Reconcile — return confirmed',
  'audit.action.reconcile-reverted': 'Reconcile reverted',
  'audit.action.reconcile-mark-failed': 'Marked failed during reconcile',
  'audit.action.push-subscription-set': 'Push subscription set',
  'audit.action.push-subscription-reassigned': 'Push subscription reassigned',
  'audit.action.push-subscription-cleared': 'Push subscription cleared',
  'audit.action.push-channel-cleared': 'Push channel cleared',
  'audit.action.fcm-token-set': 'FCM token set',
  'audit.action.fcm-token-reassigned': 'FCM token reassigned',
  'audit.action.fcm-token-cleared': 'FCM token cleared',
  'audit.action.locale-updated': 'Language preference updated',
  'audit.action.retention-anonymize-batch': 'Retention — anonymize batch',
  'audit.action.retention-anonymize-consent-batch': 'Retention — consent anonymize batch',
  'audit.action.retention-purge-batch': 'Retention — purge batch',
  'saleDetail.viewAudit': 'Audit trail',
  'sales.search.placeholder': 'Search code, title or location…',
  'pagination.range': '{from}–{to} / {total}',
  'pagination.prev': 'Previous page',
  'pagination.next': 'Next page',
  'login.title': 'Admin — Sign in',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.failed': 'Sign-in failed',
  'reset.forgotLink': 'Forgot my password',
  'reset.forgotTitle': 'Reset your password',
  'reset.forgotIntro':
    'Enter your account email address. If an account exists, a reset link will be sent to it.',
  'reset.forgotSubmit': 'Send reset link',
  'reset.forgotSent':
    'If an account exists, a reset link has been sent to that address. The link is valid for 30 minutes.',
  'reset.resetTitle': 'Choose a new password',
  'reset.newPassword': 'New password',
  'reset.confirmPassword': 'New password (again)',
  'reset.submit': 'Update password',
  'reset.mismatch': 'The passwords do not match',
  'reset.tooShort': 'The password must be at least 8 characters',
  'reset.done': 'Your password has been updated. You can now sign in with it.',
  'reset.backToLogin': 'Back to sign in',
  'reset.missingToken': 'This link is not valid. Start the reset again.',
  'reset.failed': 'Could not update the password',
  'reset.invalidToken':
    'This reset link is invalid or has expired. Start the reset again.',
  'sales.title': 'Auctions',
  'sales.new': 'New auction',
  'sales.tab.future': 'Upcoming',
  'sales.tab.past': 'History',
  'sales.tab.draft': 'Draft',
  'sales.col.code': 'Auction code',
  'sales.col.title': 'Auction name',
  'sales.col.style': 'Flow',
  'sales.col.start': 'Starts',
  'sales.col.lots': 'Vehicles',
  'sales.col.status': 'Status',
  'sales.cancelTitle': 'Cancel auction',
  'sales.empty': 'No auctions in this tab.',
  'sales.emptyFiltered': 'No auctions match the selected filters.',
  'sales.filter.status': 'Status',
  'sales.filter.from': 'Starts on or after',
  'sales.filter.to': 'Starts on or before',
  'sales.filter.clear': 'Clear filters',
  'saleState.DRAFT': 'Draft',
  'saleState.PUBLISHED': 'Published',
  'saleState.LIVE': 'Live',
  'saleState.PAUSED': 'Paused',
  'saleState.CLOSED': 'Completed',
  'saleState.CANCELLED': 'Cancelled',
  'newSale.title': 'New auction',
  'newSale.titleField': 'Auction name',
  'newSale.draftSaved': 'Draft saved · {time}',
  'newSale.draftFailed': 'Draft could not be saved',
  'newSale.vehicleFilter': 'Filter by plate or brand…',
  'newSale.col.plate': 'Plate',
  'newSale.col.vehicle': 'Vehicle',
  'newSale.col.start': 'Starting',
  'newSale.col.reserve': 'Reserve',
  'saleDetail.export': 'Export XLSX',
  'saleDetail.bidCountShort': '{n} bids',
  'saleDetail.remainingShort': '{time} left',
  'saleDetail.noDeposits': 'No deposits for this auction.',
  'saleDetail.reserve.help':
    'The reserve is the hidden floor price bidders never see. When the top bid reaches it, the vehicle flips to a guaranteed sale automatically.',
  'saleDetail.reserve.current': 'Current reserve: {amount}',
  'saleDetail.reserve.none': 'No reserve is set for this vehicle.',
  'saleDetail.reserve.crossedTitle': 'Reserve crossed',
  'saleDetail.reserve.crossedHelp':
    'The top bid has passed the reserve, so the vehicle is in guaranteed sale. Setting the reserve again does not undo that \u2014 it still sells to the highest bidder.',
  'saleDetail.reserve.guaranteedHelp':
    'This vehicle opened as a guaranteed sale; it goes to the highest bidder regardless of the reserve.',
  'saleDetail.reserve.lowerHint':
    'Lowering the reserve makes the vehicle sellable; raising it only has an effect while it has not been crossed.',
  'saleDetail.control.on': 'You have control',
  'saleDetail.control.help':
    'With control taken you can close the lot at any moment with \u201cHammer\u201d, and every bid in the last 30 seconds extends the clock by 30 seconds. Release it and the lot closes automatically at its own end time.',
  'saleDetail.noBids': 'No bids yet.',
  'saleDetail.dialog.invalidAmount': 'Enter a valid amount',
  'saleDetail.dialog.mustExceedTop': 'Must exceed the current top bid',
  'saleDetail.dialog.reAddGuaranteed': 'Add as a guaranteed sale',
  'saleDetail.dialog.reAddReserveHint': 'Leave blank to inherit the source lot’s reserve.',

  'firms.title': 'Firms',
  'firms.new': 'New firm',
  'firms.edit': 'Edit firm',
  'firms.searchPlaceholder': 'Search name, tax number or email…',
  'firms.showArchived': 'Show archived',
  'firms.empty': 'No firms yet.',
  'firms.notFound': 'Firm not found.',
  'firms.col.name': 'Firm',
  'firms.col.taxNumber': 'Tax no',
  'firms.col.contact': 'Contact',
  'firms.col.keys': 'Keys',
  'firms.field.name': 'Firm name',
  'firms.field.taxAdmin': 'Tax office',
  'firms.field.taxNumber': 'Tax number',
  'firms.field.contactEmail': 'Contact email',
  'firms.field.contactPhone': 'Phone',
  'firms.field.address': 'Address',
  'firms.field.editorial': 'Editorial firm',
  'firms.created': 'Firm created',
  'firms.updated': 'Updated',
  'firms.archive': 'Archive',
  'firms.archived': 'Archived',
  'firms.archivedBadge': 'Archived',
  'firms.archiveConfirm':
    '{name} will be archived and every API key it holds revoked. The firm stops being able to submit vehicles immediately.',
  'firms.restore': 'Restore',
  'firms.restored': 'Restored',
  'firms.restoreNote': 'Restoring does not bring old keys back — issue a new one.',
  'firms.keys.title': 'API keys',
  'firms.keys.intro':
    'The key the firm’s own system uses to submit vehicles. You issue it and hand it over.',
  'firms.keys.new': 'Issue key',
  'firms.keys.label': 'Label (optional)',
  'firms.keys.labelPlaceholder': 'e.g. stock-service',
  'firms.keys.empty': 'This firm has no keys.',
  'firms.keys.col.label': 'Label',
  'firms.keys.col.created': 'Created',
  'firms.keys.col.lastUsed': 'Last used',
  'firms.keys.col.state': 'State',
  'firms.keys.state.active': 'Active',
  'firms.keys.state.revoked': 'Revoked',
  'firms.keys.neverUsed': 'Never used',
  'firms.keys.revoke': 'Revoke',
  'firms.keys.revoked': 'Key revoked',
  'firms.keys.revokeConfirm':
    'This key stops working immediately and cannot be restored. The firm cannot submit until its integration moves to a new key.',
  'firms.keys.createdTitle': 'Key issued',
  'firms.keys.createdWarning':
    'This key is shown once — only its hash is stored. Copy it now and hand it over through a secure channel.',
  'firms.keys.archivedNote': 'An archived firm cannot be issued keys.',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.all': 'All',
  'users.title': 'Users',
  'users.searchPlaceholder': 'Search email or name…',
  'users.roleGroup.staff': 'Staff',
  'users.roleGroup.firm': 'Firm',
  'users.roleGroup.participant': 'Participant',
  'users.filter.search': 'Search',
  'users.filter.role': 'Role',
  'users.filter.roleAll': 'All roles',
  'users.filter.firm': 'Firm',
  'users.filter.firmAll': 'All firms',
  'users.filter.firmNone': 'No firm',
  'users.filter.membership': 'Membership',
  'users.filter.membershipAll': 'All memberships',
  'users.filter.membershipNone': 'No membership',
  'users.filter.clear': 'Clear filters',
  'users.count': '{n} users',
  'users.noMatch': 'No users match the filters.',
  'companyPicker.search': 'Search companies…',
  'companyPicker.empty': 'No companies found.',
  'companyPicker.remove': 'Remove {name}',
  'config.title': 'Settings',
  'config.unsaved': 'Unsaved changes',
  'config.removePackage': 'Remove package',
  'config.removePackageConfirm': 'The package will be removed from the list; the change persists on Save.',
  'submissions.title': 'Vehicle submissions',
  'submissions.subtitle':
    'Vehicles partner firms sent in from their own systems. Approving one adds it to its target auction as a “Scheduled” lot; a rejected one never enters the auction.',
  'submissions.col.company': 'Submitting firm',
  'submissions.empty.queued': 'No queued submissions.',
  'submissions.empty.approved': 'No approved submissions.',
  'submissions.empty.rejected': 'No rejected submissions.',
  'submissions.tab.queued': 'Queued',
  'submissions.tab.approved': 'Approved',
  'submissions.tab.rejected': 'Rejected',
  'submissions.status.queued': 'Queued',
  'submissions.status.scheduled': 'Scheduled',
  'submissions.status.live': 'Live',
  'submissions.status.sold': 'Sold',
  'submissions.status.unsold': 'Unsold',
  'submissions.status.rejected': 'Rejected',
  'submissions.review': 'Review',
  'submissions.detailTitle': 'Submission detail',
  'submissions.detail.clientRef': 'Client reference',
  'submissions.detail.created': 'Submitted',
  'submissions.detail.decided': 'Decided',

  // === Bidder web (M7) — full en ============================================
  'common.yes': 'Yes',
  'common.dayShort': 'd',
  'common.no': 'No',
  // Shared labels the bidder routes render (showcase status, closed lots, My Bids, titles) —
  // previously tr-only, so English mode fell back to Turkish for them.
  'sales.untitled': '(untitled)',
  'presentation.draft': 'Draft',
  'presentation.preparing': 'Preparing',
  'presentation.inprogress': 'In progress',
  'presentation.paused': 'Paused',
  'presentation.done': 'Completed',
  'presentation.cancelled': 'Cancelled',
  'lotState.SCHEDULED': 'Queued',
  'lotState.OPEN': 'Open',
  'lotState.SOLD': 'Sold',
  'lotState.UNSOLD': 'Unsold',
  'lotState.REMOVED': 'Removed',
  'bidKind.bid': 'Bid',
  'bidKind.early-bid': 'Early bid',
  'bidKind.free-bid': 'Free bid',
  'bidder.appTitle': '{brand} Auctions',
  'bidder.meta.description':
    '{brand} auction platform — follow vehicle auctions live and place your bids.',
  'bidder.nav.auctions': 'Auctions',
  'bidder.nav.dashboard': 'My panel',
  'bidder.nav.myBids': 'My bids',
  'bidder.nav.membership': 'Membership',
  'bidder.nav.login': 'Sign in',
  'bidder.nav.register': 'Sign up',
  'bidder.nav.logout': 'Log out',
  'bidder.lang.label': 'Language',
  'bidder.site.home': 'Home',
  'bidder.site.about': 'About us',
  'bidder.site.services': 'Services',
  'bidder.site.contact': 'Contact',
  'bidder.site.legal': 'Privacy & legal texts',
  'bidder.footer.tagline':
    'Integrated vehicle operations, fleet management, trading and legal advisory services across all 81 provinces of Türkiye.',
  'bidder.footer.quickLinks': 'Quick access',
  'bidder.footer.corporate': 'Corporate',
  'bidder.footer.contact': 'Contact us',
  'bidder.footer.follow': 'Follow us',
  'bidder.footer.rights': '© {year} {company} · All rights reserved',
  'bidder.header.contactBar': 'Contact details',
  'bidder.notif.enable': 'Enable notifications',
  'bidder.notif.disable': 'Disable notifications',
  'bidder.notif.enabledToast': 'Notifications enabled',
  'bidder.notif.disabledToast': 'Notifications disabled',
  'bidder.notif.blocked': 'Notifications are blocked in your browser settings.',
  'bidder.notif.failed': 'Could not enable notifications',
  'bidder.showcase.title': 'Auctions',
  'bidder.showcase.empty': 'No auctions to show right now.',
  'bidder.showcase.subline': '{n} auctions · live updates',
  'bidder.showcase.subline_one': '{n} auction · live updates',
  'bidder.showcase.subline_other': '{n} auctions · live updates',
  'bidder.showcase.lots': '{n} vehicles',
  'bidder.showcase.lots_one': '{n} vehicle',
  'bidder.showcase.lots_other': '{n} vehicles',
  'bidder.showcase.view': 'View vehicles',
  'bidder.showcase.membersOnly': 'Auctions are for members',
  'bidder.showcase.loginPrompt': 'Sign in to see the auctions, their vehicles and their results.',
  'bidder.showcase.remaining': 'Left',
  'bidder.showcase.startsIn': 'Starts in',
  'bidder.showcase.stats.salesHeld': 'Auctions held',
  'bidder.showcase.stats.vehiclesSold': 'Vehicles sold',
  'bidder.login.title': 'Sign in',
  'bidder.login.noAccount': 'No account?',
  'bidder.login.registerLink': 'Sign up',
  'bidder.register.title': 'Sign up',
  'bidder.register.first': 'First name',
  'bidder.register.last': 'Last name',
  'bidder.register.email': 'Email',
  'bidder.register.password': 'Password',
  'bidder.register.mobile': 'Phone (optional)',
  'bidder.register.submit': 'Create account',
  'bidder.register.submitting': 'Creating…',
  'bidder.register.failed': 'Sign-up failed',
  'bidder.register.consentIntro': 'To continue, accept the documents below:',
  'bidder.register.section.identity': 'Identity',
  'bidder.register.section.contact': 'Contact',
  'bidder.register.section.password': 'Password',
  'bidder.register.consentAydinlatmaPre': 'I have read the ',
  'bidder.register.consentAydinlatmaDoc': 'KVKK Disclosure Notice',
  'bidder.register.consentAydinlatmaPost': '.',
  'bidder.register.consentAcikRizaPre': 'I accept the ',
  'bidder.register.consentAcikRizaDoc': 'Explicit Consent Notice',
  'bidder.register.consentAcikRizaPost': '.',
  'bidder.register.consentRequired': 'You must accept both documents.',
  'bidder.register.pendingTitle': 'Application received',
  'bidder.register.pendingBody':
    'Your membership application has been sent for review. We will email you once it is approved; you cannot sign in until then.',
  'bidder.login.pending': 'Your membership application is still under review. We will let you know once it is approved.',
  'bidder.login.rejected': 'Your membership application was not approved. Please contact us for details.',
  'bidder.register.haveAccount': 'Already have an account?',
  'bidder.register.loginLink': 'Sign in',
  'bidder.detail.back': '← Back to auctions',
  'bidder.detail.startingPrice': 'Starting',
  'bidder.detail.currentBid': 'Current bid',
  'bidder.detail.bidCount': 'Bids',
  'bidder.detail.remaining': 'Time left',
  'bidder.detail.guaranteed': 'Guaranteed sale',
  'bidder.detail.reserveMet': 'Price goal reached',
  'bidder.detail.bid': '+1 Bid',
  'bidder.detail.jump': 'Raise to target',
  'bidder.detail.early': 'Early bid',
  'bidder.detail.deposit': 'Pay deposit',
  'bidder.detail.depositNeeded': 'A deposit is required to bid',
  'bidder.detail.goldBypass': 'Your membership needs no deposit',
  'bidder.detail.joinConf': 'Join conference →',
  'bidder.detail.closed': 'Closed',
  'bidder.detail.sold': 'Sold',
  'bidder.detail.won': 'You won!',
  'bidder.detail.outbid': 'You were outbid',
  'bidder.detail.actionFailed': 'Action failed',
  'bidder.detail.bidCountShort': '{n} bids', // base: used when no numeric `n` is supplied
  'bidder.detail.bidCountShort_one': '{n} bid',
  'bidder.detail.bidCountShort_other': '{n} bids',
  'bidder.detail.view': 'View',
  'bidder.detail.remainingShort': '{time} left',
  'bidder.detail.mustExceedTop': 'Must exceed the current top bid',
  'bidder.detail.earlyInvalid': 'Enter a valid step count',
  'bidder.detail.jumpPrompt': 'Target amount ({currency}):',
  'bidder.detail.earlyPrompt': 'How many steps (early bid)?',
  'bidder.detail.loginToBid': 'Sign in to bid',
  'bidder.detail.confirmTitle': 'Confirm your bid',
  'bidder.detail.confirmAmount': 'Your bid: {amount}',
  'bidder.detail.binding': 'A bid is binding and cannot be withdrawn.',
  'bidder.detail.jumpRounding':
    'Your amount is rounded down to the nearest valid increment step.',
  'bidder.detail.confirmBid': 'Place bid',
  'bidder.detail.priceMoved':
    'The price changed while you were confirming. Check the new amount and try again.',
  'bidder.deposit.title': 'Deposit',
  'bidder.deposit.holder': 'Cardholder',
  'bidder.deposit.number': 'Card number',
  'bidder.deposit.expiry': 'Expiry (MM/YY)',
  'bidder.deposit.cvv': 'CVV',
  'bidder.deposit.submit': 'Pay',
  'bidder.myBids.title': 'My bids',
  'bidder.myBids.empty': 'You have no bids yet.',
  'bidder.myBids.sale': 'Auction',
  'bidder.myBids.plate': 'Plate',
  'bidder.myBids.amount': 'Amount',
  'bidder.myBids.kind': 'Kind',
  'bidder.myBids.time': 'Time',
  'bidder.myBids.perPage': 'Per page',
  'bidder.myBids.viewVehicle': 'View vehicle',
  'bidder.myBids.subline': '{n} bids · newest first',
  'bidder.myBids.subline_one': '{n} bid · newest first',
  'bidder.myBids.subline_other': '{n} bids · newest first',
  'bidder.dashboard.title': 'My panel',
  'bidder.dashboard.sublineSales': '{n} auctions',
  'bidder.dashboard.sublineSales_one': '{n} auction',
  'bidder.dashboard.sublineSales_other': '{n} auctions',
  'bidder.dashboard.sublineBids': '{n} bids',
  'bidder.dashboard.sublineBids_one': '{n} bid',
  'bidder.dashboard.sublineBids_other': '{n} bids',
  'bidder.dashboard.empty': 'You have not joined an auction yet.',
  'bidder.dashboard.section.participation': 'My participation',
  'bidder.dashboard.sales': 'Auctions joined',
  'bidder.dashboard.lots': 'Vehicles bid on',
  'bidder.dashboard.bids': 'Bids placed',
  'bidder.dashboard.section.results': 'My results',
  'bidder.dashboard.won': 'Won',
  'bidder.dashboard.lost': 'Lost',
  'bidder.dashboard.pending': 'In progress',
  'bidder.dashboard.pendingHint': 'Vehicles not yet decided',
  'bidder.dashboard.leading': 'Currently leading',
  'bidder.dashboard.leadingHint': 'You hold the top bid right now',
  'bidder.dashboard.recent': 'Your recent bids',
  'bidder.dashboard.viewAll': 'View all',
  'bidder.membership.title': 'Membership packages',
  'bidder.membership.subline': '{n} packages',
  'bidder.membership.subline_one': '{n} package',
  'bidder.membership.subline_other': '{n} packages',
  'bidder.membership.empty': 'No packages available right now.',
  'bidder.membership.buy': 'Buy',
  'bidder.membership.alreadyMember': 'You are already a member',
  'bidder.membership.purchaseFailed': 'Purchase failed',
  'bidder.payment.successTitle': 'Payment successful',
  'bidder.payment.successBody': 'Your transaction is complete.',
  'bidder.payment.failTitle': 'Payment failed',
  'bidder.payment.failBody': 'Your transaction could not be completed.',
  'bidder.payment.pendingTitle': 'Payment processing',
  'bidder.payment.pendingBody': 'Your transaction is being verified, please wait.',
  'bidder.payment.backHome': 'Back to auctions',
  'push.outbid.title': 'You were outbid',
  'push.outbid.body': 'A higher bid was placed on a vehicle you follow.',
  'push.youWon.title': 'Congratulations! You won the auction',
  'push.youWon.body': '{plate} is yours.',
  'push.youWon.bodyNoPlate': 'You won a vehicle.',
  'push.digest.title': "Today's Auctions",
  'push.digest.body': '{count} auctions are waiting for you today.',

  // --- M11 vehicle detail -------------------------------------------------
  'nav.vehicles': 'Vehicles',
  'vehicles.title': 'Vehicles',
  'vehicles.new': 'New vehicle',
  'vehicles.empty': 'No vehicles yet',
  'vehicles.search.placeholder': 'Plate, brand, model…',
  'vehicles.count': '{n} vehicles',
  'vehicles.col.photo': 'Photo',
  'vehicles.col.plate': 'Plate',
  'vehicles.col.vehicle': 'Vehicle',
  'vehicles.col.yearKm': 'Year / km',
  'vehicles.col.price': 'Price',
  'vehicles.col.status': 'Status',
  'vehicles.col.auction': 'Auction',
  'vehicles.status.available': 'Available',
  'vehicles.status.committed': 'In auction',
  'vehicles.filter.search': 'Search',
  'vehicles.filter.auction': 'Auction',
  'vehicles.filter.auction.none': 'Never auctioned',
  'vehicles.filter.status': 'Status',
  'vehicles.filter.firm': 'Firm',
  'vehicles.filter.firm.none': 'No firm',
  'vehicles.filter.year': 'Model year',
  'vehicles.filter.yearMin': 'Earliest model year',
  'vehicles.filter.yearMax': 'Latest model year',
  'vehicles.filter.yearInvalid': 'Enter a four-digit year between 1900 and 2999',
  'vehicles.filter.yearRangeInvalid': 'The first year cannot be later than the last',
  'vehicles.filter.active': '{n} filters on',
  'vehicles.filter.clear': 'Clear filters',
  'vehicles.emptyFiltered': 'No vehicles match these filters',
  'vehicles.emptyFilteredHint': 'Clear the filters and try again.',
  'vehicles.created': 'Vehicle created',
  'vehicles.updated': 'Vehicle updated',
  'vehicles.deleted': 'Vehicle deleted',
  'vehicles.deleteTitle': 'Delete vehicle',
  'vehicles.deleteBody': 'Vehicle {plate} will be permanently deleted. This cannot be undone.',
  'vehicles.saveFailed': 'Could not save the vehicle',
  'vehicles.deleteFailed': 'Could not delete the vehicle',
  'vehicles.notFound': 'Vehicle not found',
  'vehicles.unsavedTitle': 'Unsaved changes',
  'vehicles.unsavedBody': 'Leaving this page will discard your changes.',
  'vehicles.section.identity': 'Identity',
  'vehicles.section.technical': 'Technical',
  'vehicles.section.equipment': 'Equipment',
  'vehicles.section.condition': 'Condition',
  'vehicles.section.expertise': 'Expertise',
  'vehicles.section.description': 'Description',
  'vehicles.section.pricing': 'Pricing',
  'vehicles.section.media': 'Media',
  'vehicles.field.plate': 'Plate',
  'vehicles.field.oldPlate': 'Previous plate',
  'vehicles.field.chassis': 'Chassis no',
  'vehicles.field.motor': 'Engine no',
  'vehicles.field.code': 'Vehicle code',
  'vehicles.field.vehicleType': 'Vehicle type',
  'vehicles.field.disability': 'Disability vehicle',
  'vehicles.field.brand': 'Brand',
  'vehicles.field.series': 'Series',
  'vehicles.field.modelName': 'Model',
  'vehicles.field.year': 'Year',
  'vehicles.field.color': 'Colour',
  'vehicles.field.km': 'Km',
  'vehicles.field.condition': 'Condition',
  'vehicles.field.fuel': 'Fuel',
  'vehicles.field.gear': 'Transmission',
  'vehicles.field.bodyType': 'Body type',
  'vehicles.field.engineCapacity': 'Engine capacity (cc)',
  'vehicles.field.enginePower': 'Engine power (hp)',
  'vehicles.field.pledgeBank': 'Pledge bank',
  'vehicles.field.fuelStatus': 'Fuel level (%)',
  'vehicles.field.insuranceValidUntil': 'Insurance valid until',
  'vehicles.field.inspectionValidUntil': 'Inspection valid until',
  'vehicles.field.description': 'Auction description',
  'vehicles.field.startingPrice': 'Starting price',
  'vehicles.field.reserve': 'Hidden reserve',
  'vehicles.field.increment': 'Increment',
  'vehicles.field.company': 'Company',
  'vehicles.field.clientRef': 'Client reference',
  'vehicles.hint.modelName': 'Brands are a managed list; the model is free text.',
  'vehicles.hint.reserve': 'Never shown to bidders.',
  'vehicles.priceLockedTitle': 'Price fields are locked',
  'vehicles.priceLockedBody': 'This vehicle is on a scheduled or running lot. Price fields cannot change until the auction ends; everything else is still editable.',
  'vehicleType.automobile': 'Car',
  'vehicleType.suv': 'SUV',
  'vehicleType.commercial': 'Commercial',
  'vehicleType.heavy-commercial': 'Heavy commercial',
  'vehicleType.motorcycles': 'Motorcycle',
  'vehicleType.minivans': 'Minivan',
  'vehicleType.bus': 'Bus',
  'vehicleType.truck': 'Truck',
  'vehicleType.damaged': 'Damaged',
  'vehicleType.earth-movers': 'Earth mover',
  'fuel.gasoline': 'Petrol',
  'fuel.gasoline-lpg': 'Petrol & LPG',
  'fuel.diesel': 'Diesel',
  'fuel.lpg': 'LPG',
  'fuel.hybrid': 'Hybrid',
  'fuel.electric': 'Electric',
  'gear.automatic': 'Automatic',
  'gear.semi-automatic': 'Semi-automatic',
  'gear.tiptronic': 'Tiptronic',
  'gear.manual': 'Manual',
  'gear.cvt': 'CVT',
  'gear.s-tronic': 'S-tronic',
  'bodyType.Cabrio': 'Cabrio',
  'bodyType.Coupe': 'Coupe',
  'bodyType.Hatchback 3-door': 'Hatchback 3-door',
  'bodyType.Hatchback 5-door': 'Hatchback 5-door',
  'bodyType.Sedan': 'Sedan',
  'bodyType.Station Wagon': 'Station wagon',
  'bodyType.Crossover': 'Crossover',
  'bodyType.MPV': 'MPV',
  'bodyType.Roadster': 'Roadster',
  'bodyType.Off-road': 'Off-road',
  'vehicleCondition.new': 'New',
  'vehicleCondition.used': 'Used',
  'vehicleCondition.free-certified': 'Certified',
  'expertise.title': 'Paint & replaced panels',
  'expertise.disclaimer': 'Paint and panel-replacement information is for reference only and is outside our responsibility.',
  'expertise.original': 'Original',
  'expertise.originalBadge': 'Original',
  'expertise.notAssessed': 'Not assessed',
  'expertise.notAssessedHint': 'A panel left blank counts as not assessed; a vehicle earns the "Original" badge only when all 14 panels are marked original.',
  'expertise.part.frontBumper': 'Front bumper',
  'expertise.part.frontHood': 'Bonnet',
  'expertise.part.roof': 'Roof',
  'expertise.part.frontRightMudguard': 'Front right wing',
  'expertise.part.frontRightDoor': 'Front right door',
  'expertise.part.rearRightDoor': 'Rear right door',
  'expertise.part.rearRightMudguard': 'Rear right wing',
  'expertise.part.frontLeftMudguard': 'Front left wing',
  'expertise.part.frontLeftDoor': 'Front left door',
  'expertise.part.rearLeftDoor': 'Rear left door',
  'expertise.part.rearLeftMudguard': 'Rear left wing',
  'expertise.part.rearHood': 'Boot',
  'expertise.part.rearBumper': 'Rear bumper',
  'expertise.part.spareTire': 'Spare wheel',
  'expertise.state.original': 'Original',
  'expertise.state.changed': 'Replaced',
  'expertise.state.painted': 'Painted',
  'expertise.state.local-painted': 'Locally painted',
  'expertise.state.crushed': 'Dented',
  'expertise.state.sketched': 'Scratched',
  'expertise.state.registered': 'Chassis-recorded',
  'equipmentKind.internal': 'Interior',
  'equipmentKind.external': 'Exterior',
  'equipmentKind.security': 'Safety',
  'equipmentKind.multimedia': 'Multimedia',
  'equipmentKind.other': 'Other',
  'checklist.title': 'Handed over with the vehicle',
  'checklist.hint': 'An unticked item means "not recorded", not "absent".',
  'checklist.warranty': 'Warranty',
  'checklist.key': 'Key',
  'checklist.spareKey': 'Spare key',
  'checklist.license': 'Registration document',
  'checklist.spareTyre': 'Spare wheel',
  'checklist.lifter': 'Jack',
  'checklist.trafficSet': 'Traffic kit',
  'checklist.chain': 'Snow chains',
  'checklist.floorMat': 'Floor mats',
  'checklist.winterTire': 'Winter tyres',
  'checklist.jackLever': 'Wheel wrench',
  'checklist.extinguisher': 'Fire extinguisher',
  'checklist.wheelScrew': 'Wheel bolts',
  'checklist.serviceManualsWarranty': 'Service book',
  'checklist.doublePlateSealControl': 'Double plate seal check',
  'checklist.firstAidKit': 'First-aid kit',
  'checklist.extraTireSet': 'Extra tyre set',


  'media.add': 'Add media',
  'media.kind': 'Media type',
  'media.kind.photo': 'Photo',
  'media.kind.damagePhoto': 'Damage photo',
  'media.kind.video': 'Video',
  'media.kind.damageVideo': 'Damage video',
  'media.cover': 'Cover',
  'media.membersOnly': 'Members only',
  'media.makeCover': 'Make cover',
  'media.showToPublic': 'Show to public',
  'media.hideFromPublic': 'Public (click to hide)',
  'media.moveLeft': 'Move left',
  'media.moveRight': 'Move right',
  'media.open': 'View full size',
  'media.viewerCounter': '{i} / {n}',
  'media.dropHint': 'Drop files here, or click "Add media"',
  'media.uploading': 'Uploading…',
  'media.uploaded': '{n} media uploaded',
  'media.updateFailed': 'Could not update the media',
  'media.deleteFailed': 'Could not delete the media',
  'media.deleteTitle': 'Delete media',
  'media.deleteBody': 'This media will be permanently deleted. This cannot be undone.',
  'media.error.decode': 'Could not read the file',
  'media.error.unencodable': 'This format cannot be resized; upload a smaller file',
  'media.error.videoType': 'Only MP4 or WebM video can be uploaded',
  'media.error.generic': 'Upload failed',
  'media.empty': 'No media yet',
  'submissions.photoCount': '{n} media',
  'submissions.noPhotos': 'No media',
  'submissions.mediaReport': 'Media mirror report',
  'submissions.mediaReportFailed': '{n} media could not be mirrored',


  'bidder.masked.title': 'Sign in to see prices',
  'bidder.masked.loginToBid': 'Sign in to bid',
  'bidder.vehicle.backToAuction': '← Back to the auction',
  'bidder.vehicle.goToRoom': 'Go to the auction room',
  'bidder.vehicle.noPhotos': 'No photos for this vehicle',
  'bidder.vehicle.openPhoto': 'Enlarge photo',

};

const dicts: Record<Lang, LocaleDict> = { tr, en };

export type Lang = 'tr' | 'en';
let current: Lang = 'tr';

/** Switch the active language (default `tr`). */
export function setLang(lang: Lang): void {
  current = lang;
}
export function getLang(): Lang {
  return current;
}

/** Resolve a message key in the active language (falling back to `tr`), filling `{param}`s. */
export function t(key: MessageKey, params?: Record<string, string | number>): string {
  return tf(current, key, params);
}

/**
 * Resolve a message key in an **explicit** language (falling back to `tr`), filling `{param}`s.
 * Unlike {@link t} this takes the language as an argument rather than the module-global `current`
 * — required server-side (e.g. push notifications), where each recipient's locale differs and
 * there is no per-request active language.
 */
export function tf(
  lang: Lang,
  key: MessageKey,
  params?: Record<string, string | number>,
): string {
  const resolved = pluralKey(lang, key, params);
  let s = dicts[lang][resolved] ?? dicts[lang][key] ?? tr[key];
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return s;
}

/**
 * The plural variant of `key` for a numeric `n` param, or `key` itself.
 *
 * A count-bearing message needs different words in English for one and for many — the audit
 * caught the shipped result of not doing this ("1 bids", "1 vehicles"). Turkish has a single
 * CLDR plural category, so it is unaffected either way, which is exactly why this went
 * unnoticed: `tr` was always right and the gate only compared key *existence*.
 *
 * Opt-in per key: a dictionary that carries `key_one` / `key_other` gets them, and one that
 * does not falls through to the base key untouched. So this is additive for every existing
 * message, and `_other` is not required to exist just because `_one` does.
 *
 * `_one`/`_other` are the CLDR category names `Intl.PluralRules` returns, not our invention —
 * a locale needing `_few`/`_many` (Polish, Arabic) would be served by the same lookup.
 */
function pluralKey(
  lang: Lang,
  key: MessageKey,
  params?: Record<string, string | number>,
): MessageKey | PluralKey {
  const n = params?.['n'];
  if (typeof n !== 'number' || !Number.isFinite(n)) return key;
  const candidate: PluralKey = `${key}_${new Intl.PluralRules(lang).select(n)}`;
  return candidate in dicts[lang] ? candidate : key;
}
